import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import { makeMatcher } from '../utils/search';
import Layout from '../components/Layout';
import { usersAPI, salesAPI, productionAPI, warehouseAPI, adminToolsAPI, branchAPI,
         testDataAPI, sheetAPI, gradeAPI } from '../api';
import toast from 'react-hot-toast';
import { confirmToast, dangerConfirm } from '../components/ConfirmToast';
import { TileGrid, BackHome, TilesHead } from '../components/Tiles';
import AiThresholdsPanel from '../components/AiThresholdsPanel';
import { useNavigate } from 'react-router-dom';

// ─── Роли системы ─────────────────────────────────────────────────────────────
const ROLES = [
  { key: 'manager',    label: 'Менеджер',        icon: '🛒', route: '/manager' },
  { key: 'technolog',  label: 'Технолог',         icon: '⚙️', route: '/technolog' },
  // Технолог, который в цех не заходит: отвечает за качество, а не за
  // справочники. Роль отдельная именно поэтому — «Технолог» открывает вместе
  // с разбором брака номенклатуру, эталонные режимы и заявки, а всё это ему
  // не нужно и трогать он их не должен.
  { key: 'quality',    label: 'Технолог по качеству', icon: '🔬', route: '/quality' },
  { key: 'operator',   label: 'Оператор пресса',  icon: '🖥️', route: '/terminal' },
  { key: 'sklad_gp',   label: 'Кладовщик СГП',   icon: '📦', route: '/warehouse' },
  { key: 'director',   label: 'Директор',         icon: '📊', route: '/director' },
  { key: 'controller', label: 'Контролёр качества',icon: '🔍', route: '/sheet-terminal' },
  { key: 'buhgalter',  label: 'Бухгалтер',        icon: '🧾', route: '/buhgalter' },
];

// Роли, которые можно ВЫДАТЬ. Это не то же самое, что список выше: там у
// каждой роли есть свой рабочий экран, и по нему строятся плитки «Рабочие
// места». У администратора такого экрана нет — он и так на этой странице,
// поэтому в ROLES его не было.
//
// А выдавать эту роль надо: второго администратора (уехал в отпуск, ушёл с
// завода, сидит в другом цехе) до сих пор нельзя было завести из интерфейса
// вовсе — сервер её принимал, а выбрать в списке было нечего.
const ASSIGNABLE_ROLES = [
  ...ROLES,
  { key: 'admin', label: 'Администратор', icon: '🔧' },
];

const ROLE_LABELS = Object.fromEntries(ASSIGNABLE_ROLES.map(r => [r.key, r.label]));

// ─── UI ───────────────────────────────────────────────────────────────────────
// Остальные свойства прокидываем: карточке внутри модального окна нужен свой
// onClick, иначе клик по ней всплывает на подложку и окно закрывается само.
const Card = ({ children, style = {}, ...rest }) => (
  <div {...rest} style={{ background: 'var(--card)', borderRadius: 12, padding: 20, ...style }}>
    {children}
  </div>
);

// Поле ввода. У пароля — глазок.
//
// Показать СОХРАНЁННЫЙ пароль нельзя: в базе он лежит хэшем, и это правильно.
// Речь про тот, который набирают прямо сейчас. Администратор заводит человека,
// диктует ему пароль вслух — и диктует то, что думал, что набрал. Опечатка в
// закрытом поле выясняется назавтра, когда человек не может войти, и виноватым
// оказывается «ваша программа не пускает».
//
// Глазок по умолчанию закрыт: рядом с администратором ходят люди.
const Inp = ({ label, ...props }) => {
  const [reveal, setReveal] = useState(false);
  const isPass = props.type === 'password';
  const field = (
    <input {...props} type={isPass && reveal ? 'text' : props.type} style={{
      width: '100%', padding: '9px 12px', background: 'var(--input)',
      border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
      fontSize: 14, outline: 'none', boxSizing: 'border-box',
      // Место под глазок, чтобы длинный пароль не уезжал под него
      ...(isPass ? { paddingRight: 40 } : {}),
      ...props.style,
    }} />
  );
  return (
    <div>
      {label && <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{label}</label>}
      {isPass ? (
        <div style={{ position: 'relative' }}>
          {field}
          <button type="button" onClick={() => setReveal(v => !v)}
            title={reveal ? t('Скрыть пароль') : t('Показать пароль')}
            style={{
              position: 'absolute', right: 4, top: 0, bottom: 0, width: 34,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 15, lineHeight: 1, padding: 0,
              opacity: reveal ? 1 : 0.55,
            }}>
            {reveal ? '🙈' : '👁'}
          </button>
        </div>
      ) : field}
    </div>
  );
};

const Sel = ({ label, children, ...props }) => (
  <div>
    {label && <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{label}</label>}
    <select {...props} style={{
      width: '100%', padding: '9px 12px', background: 'var(--input)',
      border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
      fontSize: 14, outline: 'none', boxSizing: 'border-box', ...props.style,
    }}>
      {children}
    </select>
  </div>
);

const Btn = ({ children, variant = 'primary', size = 'md', ...props }) => {
  const colors = { primary: 'var(--accent)', success: '#22c55e', danger: '#ef4444', ghost: 'transparent', warning: '#f59e0b' };
  const bg = colors[variant] || colors.primary;
  return (
    <button {...props} style={{
      background: props.disabled ? 'var(--card)' : bg,
      border: variant === 'ghost' ? '1px solid var(--border)' : 'none',
      borderRadius: 8, color: props.disabled ? 'var(--dim)' : (variant === 'ghost' ? 'var(--muted)' : '#fff'),
      padding: size === 'sm' ? '6px 12px' : '9px 18px',
      fontSize: 13, fontWeight: 600, cursor: props.disabled ? 'not-allowed' : 'pointer', ...props.style,
    }}>{children}</button>
  );
};

// Окно поверх страницы.
//
// Форма правки открывалась ВВЕРХУ списка, и это сбивало: человек нажимал
// «Изменить» у седьмой строки, экран не двигался, а поля появлялись где-то
// выше — вне поля зрения. Приходилось догадываться, что вообще что-то
// произошло, и прокручивать наверх, теряя место в списке.
function Modal({ title, onClose, children, width = 900 }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: 20, overflowY: 'auto',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--card)', borderRadius: 14, width: '100%',
        maxWidth: width, marginTop: 40, marginBottom: 40,
      }}>
        <div style={{
          padding: '15px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ flex: 1, color: 'var(--text)', fontSize: 16,
                        fontWeight: 700 }}>{title}</div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--muted)',
            fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 0,
          }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Управление пользователями ────────────────────────────────────────────────
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ username: '', first_name: '', last_name: '', role: 'operator', password: '', pin: '', press: '', can_ship: false, can_plan: false,
    branch: '', all_branches: false, extra_branches: [],
    can_add_nomenclature: false, can_web_terminal: false, can_view_passport: false, can_edit_1c_settings: false });
  // Прессы для поля «рабочее место»
  const [presses, setPresses] = useState([]);
  useEffect(() => { productionAPI.getPresses().then(r => setPresses(r.data)).catch(() => {}); }, []);
  // Филиалы. Приходят только те, что разрешены самому админу: админ цеха
  // чужой в этом списке не увидит и человека туда не заведёт.
  const [branches, setBranches] = useState([]);
  useEffect(() => { branchAPI.getList().then(r => setBranches(r.data)).catch(() => {}); }, []);
  const [saving, setSaving] = useState(false);

  const load = () => usersAPI.getList().then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditUser(null);
    setForm({ username: '', first_name: '', last_name: '', role: 'operator', password: '', pin: '', press: '', can_ship: false, can_plan: false,
    branch: '', all_branches: false, extra_branches: [],
    can_add_nomenclature: false, can_web_terminal: false, can_view_passport: false, can_edit_1c_settings: false });
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ username: u.username, first_name: u.first_name, last_name: u.last_name,
      role: u.role, password: '', pin: u.pin || '', press: u.press || '',
      can_ship: !!u.can_ship, can_plan: !!u.can_plan,
      branch: u.branch || '', all_branches: !!u.all_branches,
      extra_branches: u.extra_branches || [],
      can_add_nomenclature: !!u.can_add_nomenclature,
      can_web_terminal: !!u.can_web_terminal,
      can_view_passport: !!u.can_view_passport,
      can_edit_1c_settings: !!u.can_edit_1c_settings });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!editUser && !form.username) { toast.error(t("Введите логин")); return; }
    if (!editUser && !form.password) { toast.error(t("Введите пароль")); return; }
    setSaving(true);
    try {
      if (editUser) {
        await usersAPI.update(editUser.id, form);
        toast.success(t("Пользователь обновлён"));
      } else {
        await usersAPI.create(form);
        toast.success(t("Пользователь создан"));
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setSaving(false); }
  };

  const handleDeactivate = async (u) => {
    if (!await confirmToast(`Пользователь ${u.username} — деактивировать?`, { danger: true, confirmText: 'Деактивировать' })) return;
    try {
      await usersAPI.deactivate(u.id);
      toast.success(t("Пользователь деактивирован"));
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Ошибка'); }
  };

  const handleReactivate = async (u) => {
    try {
      await usersAPI.update(u.id, { is_active: true });
      toast.success(t("Пользователь активирован"));
      load();
    } catch { toast.error(t("Ошибка")); }
  };

  const activeUsers   = users.filter(u => u.is_active);
  const inactiveUsers = users.filter(u => !u.is_active);

  // Раскладываем людей по цехам.
  //
  // Одним списком это читалось, пока цех был один. С двумя — оператор Алматы
  // стоит между шымкентскими менеджерами, и чтобы понять, кого в каком цехе
  // сколько, приходится читать бейджи в каждой строке.
  //
  // Пока цех один, заголовков не рисуем: единственная группа на весь список —
  // это не группировка, а лишняя строка.
  // Какие цеха раскрыты. Свёрнуты по умолчанию: восемнадцать человек в одном
  // цехе — это восемнадцать строк, и с тремя цехами страница уходит вниз
  // настолько, что «кого в каком цехе сколько» уже не видно. Свёрнутый список
  // отвечает на этот вопрос сразу, а разворачивают его тогда, когда ищут
  // конкретного человека.
  const [openBranch, setOpenBranch] = useState(() => new Set());
  const toggleBranch = (name) => setOpenBranch(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const groupUsers = (list) => {
    if (branches.length < 2) return [[null, list]];
    const by = new Map();
    branches.forEach(b => by.set(b.name, []));
    list.forEach(u => {
      const key = u.branch_name || '— без цеха —';
      if (!by.has(key)) by.set(key, []);
      by.get(key).push(u);
    });
    // Пустой цех показываем тоже: «в Астане ни одного человека» — это ответ,
    // а не отсутствие ответа.
    return Array.from(by.entries());
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700 }}>{t('👥 Пользователи системы')}</h2>
        <Btn onClick={openCreate}>{t('+ Новый пользователь')}</Btn>
      </div>

      {/* Форма создания / редактирования — окном поверх страницы, а не
          вверху списка: нажатие у седьмой строки не должно уводить поля
          туда, где их не видно. */}
      {showForm && (
        <Modal onClose={() => setShowForm(false)}
               title={editUser ? `Редактировать: ${editUser.username}`
                               : 'Новый пользователь'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            {!editUser && (
              <Inp label="Логин *" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="ivanov" />
            )}
            <Inp label="Имя" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder={t("Иван")} />
            <Inp label="Фамилия" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder={t("Иванов")} />
            <Inp label={editUser ? 'Новый пароль (пусто = не менять)' : 'Пароль *'} type="password"
              value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={t("Минимум 6 символов")} />
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ width: 220 }}>
              {/* Технологу право на заявку ставим сразу: он это и делал, и
                  заводить учётку без него значит, что утром работа встанет, а
                  никто не поймёт почему. Снять галочку можно тут же. */}
              <Sel label="Роль" value={form.role}
                   onChange={e => setForm({ ...form, role: e.target.value,
                     can_plan: e.target.value === 'technolog' ? true : form.can_plan })}>
                {ASSIGNABLE_ROLES.map(r => (
                  <option key={r.key} value={r.key}>{r.icon} {r.label}</option>
                ))}
              </Sel>
            </div>
            {/* У пресса один моноблок и общий вход — человека называет ПИН,
                который он вводит при старте работы. Выпуск и брак запишутся
                на него, а не на общую учётку. */}
            <div style={{ width: 190 }}>
              <Inp label="ПИН у пресса (4–6 цифр)" value={form.pin}
                onChange={e => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                placeholder={t("напр. 2408")} style={{ fontFamily: 'monospace', letterSpacing: 2 }} />
            </div>
            {/* Рабочее место. Оператор выбирал пресс из списка сам — и мог
                выбрать не тот: смена уходила на чужой станок, а замечали это
                в отчёте за месяц. Монитор у пресса стоит на месте, поэтому
                место знает учётка: под ней открывается только этот пресс,
                экрана выбора нет. Кто именно работает — отвечает ПИН. */}
            <div style={{ width: 220 }}>
              <Sel label="Рабочее место (пресс)" value={form.press}
                onChange={e => setForm({ ...form, press: e.target.value })}>
                <option value="">— выбирает сам —</option>
                {presses.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Sel>
            </div>
            {/* Главный менеджер. Менеджеров в офисе несколько, каждый ведёт
                своих клиентов, но «отдать товар» решает один: иначе машина
                уедет к тому, кто громче попросил, а товар, обещанный другому,
                уйдёт раньше срока. Право выписать распоряжение на отгрузку —
                у него. */}
            {(form.role === 'manager' || form.role === 'technolog') && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                fontSize: 13.5, color: 'var(--text)', whiteSpace: 'nowrap',
                alignSelf: 'flex-end', paddingBottom: 9,
              }}>
                <input type="checkbox" checked={!!form.can_ship}
                  onChange={e => setForm({ ...form, can_ship: e.target.checked })}
                  style={{ width: 17, height: 17 }} />
                {t('Разрешить отгрузку')}
              </label>
            )}
            {/* Заявку на производство выписывает плановый отдел: решить, какой
                заказ пустить сегодня и в какую смену. Технолог отвечает за
                другое — за режим и матрицу. Раньше ради одной этой обязанности
                человеку заводили роль «Технолог» и отдавали весь справочник. */}
            {(form.role === 'manager' || form.role === 'technolog') && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                fontSize: 13.5, color: 'var(--text)', whiteSpace: 'nowrap',
                alignSelf: 'flex-end', paddingBottom: 9,
              }}>
                <input type="checkbox" checked={!!form.can_plan}
                  onChange={e => setForm({ ...form, can_plan: e.target.checked })}
                  style={{ width: 17, height: 17 }} />
                {t('Разрешить заявку на производство')}
              </label>
            )}
            {/* Номенклатура приходит из 1С, и заведённая здесь строка туда не
                попадает: продать такой лист бухгалтерия не сможет. Но обмена
                по номенклатуре ещё нет, и без этого права менеджер не примет
                заказ на новый декор. Галочка, а не роль: когда обмен
                заработает, её снимут одним движением. */}
            {['manager', 'sklad_gp'].includes(form.role) && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                fontSize: 13.5, color: 'var(--text)', whiteSpace: 'nowrap',
                alignSelf: 'flex-end', paddingBottom: 9,
              }}>
                <input type="checkbox" checked={!!form.can_add_nomenclature}
                  onChange={e => setForm({ ...form, can_add_nomenclature: e.target.checked })}
                  style={{ width: 17, height: 17 }} />
                {t('Разрешить заводить номенклатуру')}
              </label>
            )}
            {/* Работу у пресса ведёт моноблок. Смена, начатая из браузера,
                оставляет его без номера смены: на свой start он получает «по
                этому заданию уже идёт смена» и дальше не отправляет ни одного
                листа — они оседают у него на диске. За 21–22.09.2026 так
                потерялся 191 лист, а девять смен остались открытыми и держали
                задания в состоянии «В работе».

                Поэтому по умолчанию нельзя, а галочка — на случай, когда
                моноблок в ремонте и пресс приходится вести с ноутбука.
                Смотреть терминал она не запрещает: закрыт только выбор
                пресса, с которого начинается счёт. */}
            {['operator', 'admin', 'technolog'].includes(form.role) && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                fontSize: 13.5, color: 'var(--text)', whiteSpace: 'nowrap',
                alignSelf: 'flex-end', paddingBottom: 9,
              }}>
                <input type="checkbox" checked={!!form.can_web_terminal}
                  onChange={e => setForm({ ...form, can_web_terminal: e.target.checked })}
                  style={{ width: 17, height: 17 }} />
                {t('Разрешить вести смену из браузера')}
              </label>
            )}
            <label style={{
              display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
              fontSize: 13.5, color: 'var(--text)', whiteSpace: 'nowrap',
              alignSelf: 'flex-end', paddingBottom: 9,
            }}>
              <input type="checkbox" checked={!!form.can_view_passport}
                onChange={e => setForm({ ...form, can_view_passport: e.target.checked })}
                style={{ width: 17, height: 17 }} />
              {t('Полный паспорт изделия')}
            </label>
            {/* Настройки обмена с 1С одни на весь завод. Галочку видит и
                выдаёт только главный администратор, и только админу цеха. */}
            {form.role === 'admin' && sessionStorage.getItem('is_super_admin') === '1' && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                fontSize: 13.5, color: 'var(--text)', whiteSpace: 'nowrap',
                alignSelf: 'flex-end', paddingBottom: 9,
              }}>
                <input type="checkbox" checked={!!form.can_edit_1c_settings}
                  onChange={e => setForm({ ...form, can_edit_1c_settings: e.target.checked })}
                  style={{ width: 17, height: 17 }} />
                {t('Изменять настройки интеграции 1С')}
              </label>
            )}
            {/* Филиал. Пока цех один, выбирать не из чего — поле и не рисуем:
                пустой список выглядит как забытая настройка. */}
            {branches.length > 1 && (
              <Sel label="Филиал" value={form.branch}
                   onChange={e => setForm({ ...form, branch: e.target.value })}>
                <option value="">{t('— выберите цех —')}</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </Sel>
            )}
            {/* Кто смотрит завод целиком. Не должность, а человек: главный
                директор и главный администратор одни, остальные сидят каждый
                в своём цехе. Филиал этому человеку всё равно нужен — он его
                рабочее место.

                Подписи про уровни выше — про клиентов платформы, про
                поставщика программы — здесь не место. Для администратора
                завода он САМ старший, и лишнее слово об этом только
                напоминало бы, что над ним кто-то есть. */}
            {/* Технолог по качеству — тоже: он сидит удалённо и сравнивает
                цеха между собой. Роль только смотрит, а писать галочка и так
                не даёт — запись всегда ограничена своим цехом (core/perms.py). */}
            {branches.length > 1
              && ['director', 'admin', 'buhgalter', 'quality'].includes(form.role) && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                fontSize: 13.5, color: 'var(--text)', whiteSpace: 'nowrap',
                alignSelf: 'flex-end', paddingBottom: 9,
              }}>
                <input type="checkbox" checked={!!form.all_branches}
                  onChange={e => setForm({ ...form, all_branches: e.target.checked })}
                  style={{ width: 17, height: 17 }} />
                {t('Видит все цеха')}
              </label>
            )}
            {/* Соседние цеха поимённо. Нужно тому, кто ведёт не весь завод, но
                больше одного цеха: «этот директор смотрит ещё и Алматы».
                Тем, у кого стоит «видит все», выбирать нечего — у них уже всё. */}
            {branches.length > 1 && !form.all_branches
              && ['director', 'admin', 'buhgalter', 'manager', 'quality'].includes(form.role) && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', color: 'var(--muted)',
                                fontSize: 12, marginBottom: 4 }}>
                  {t('Видит ещё эти цеха')}
                </label>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  {branches.filter(b => String(b.id) !== String(form.branch))
                    .map(b => (
                    <label key={b.id} style={{ display: 'flex', alignItems: 'center',
                                               gap: 7, cursor: 'pointer',
                                               fontSize: 13.5, color: 'var(--text)' }}>
                      <input type="checkbox"
                        checked={(form.extra_branches || []).includes(b.id)}
                        onChange={e => setForm({ ...form,
                          extra_branches: e.target.checked
                            ? [...(form.extra_branches || []), b.id]
                            : (form.extra_branches || []).filter(x => x !== b.id) })}
                        style={{ width: 16, height: 16 }} />
                      {b.name}
                    </label>
                  ))}
                  {branches.length < 2 && (
                    <span style={{ color: 'var(--dim)', fontSize: 12 }}>
                      других цехов нет
                    </span>
                  )}
                </div>
              </div>
            )}
            <Btn onClick={handleSave} disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить'}</Btn>
            <Btn variant="ghost" onClick={() => setShowForm(false)}>{t('Отмена')}</Btn>
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
            ПИН нужен тем, кто работает у пресса. Пусто — человек ПИНом не
            пользуется. Два одинаковых ПИНа система не примет: выпуск записался
            бы не на того.
          </div>
        </Modal>
      )}

      {/* Список активных пользователей */}
      <Card>
        {groupUsers(activeUsers).map(([branchName, list]) => (
        <div key={branchName || 'all'} style={{ marginBottom: branchName ? 14 : 0 }}>
          {branchName && (
            <button onClick={() => list.length && toggleBranch(branchName)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                marginBottom: 8, background: 'none', border: 'none', padding: 0,
                cursor: list.length ? 'pointer' : 'default',
                fontFamily: 'inherit', textAlign: 'left',
              }}>
              {/* Пустой цех не раскрывается: раскрывать там нечего, и стрелка
                  обещала бы содержимое, которого нет */}
              <span style={{ color: 'var(--dim)', fontSize: 12, width: 10 }}>
                {list.length ? (openBranch.has(branchName) ? '▾' : '▸') : ''}
              </span>
              <span style={{ color: 'var(--text)', fontSize: 13.5,
                             fontWeight: 800 }}>{branchName}</span>
              <span style={{ color: 'var(--dim)', fontSize: 12 }}>
                {list.length ? `${list.length} чел.` : 'никого'}
              </span>
              <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </button>
          )}
        <div style={{ display: branchName && !openBranch.has(branchName)
                        ? 'none' : 'flex',
                      flexDirection: 'column', gap: 6 }}>
          {list.map(u => (
            <div key={u.id} style={{
              background: 'var(--bg)', borderRadius: 10, padding: '12px 16px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'var(--accent-weak)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18,
                }}>
                  {ASSIGNABLE_ROLES.find(r => r.key === u.role)?.icon || '👤'}
                </div>
                <div>
                  <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14 }}>
                    {u.first_name || u.last_name ? `${u.first_name} ${u.last_name}`.trim() : u.username}
                    <span style={{ color: 'var(--dim)', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>@{u.username}</span>
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
                    {ROLE_LABELS[u.role] || u.role}
                    {/* ПИН показываем открыто: это бейдж, а не пароль —
                        админ должен уметь назвать его забывшему человеку */}
                    {u.pin && (
                      <span style={{
                        marginLeft: 8, background: 'var(--accent-weak)', color: 'var(--accent)',
                        borderRadius: 6, padding: '1px 8px', fontFamily: 'monospace',
                        fontWeight: 700, letterSpacing: 1,
                      }}>ПИН {u.pin}</span>
                    )}
                    {u.can_ship && (
                      <span style={{
                        marginLeft: 8, background: '#8b5cf618', color: '#8b5cf6',
                        borderRadius: 6, padding: '1px 8px', fontWeight: 700,
                      }}>🚚 отгрузка</span>
                    )}
                    {u.can_plan && (
                      <span style={{
                        marginLeft: 8, background: '#22c55e18', color: '#22c55e',
                        borderRadius: 6, padding: '1px 8px', fontWeight: 700,
                      }}>✅ заявка</span>
                    )}
                    {u.can_add_nomenclature && (
                      <span style={{
                        marginLeft: 8, background: '#0ea5e918', color: '#0ea5e9',
                        borderRadius: 6, padding: '1px 8px', fontWeight: 700,
                      }}>📦 номенклатура</span>
                    )}
                    {u.can_web_terminal && (
                      <span style={{
                        marginLeft: 8, background: '#f59e0b18', color: '#f59e0b',
                        borderRadius: 6, padding: '1px 8px', fontWeight: 700,
                      }}>🖥️ смена из браузера</span>
                    )}
                    {u.can_view_passport && (
                      <span style={{
                        marginLeft: 8, background: '#8b5cf618', color: '#8b5cf6',
                        borderRadius: 6, padding: '1px 8px', fontWeight: 700,
                      }}>{t('📋 полный паспорт')}</span>
                    )}
                    {/* Чей человек — видно сразу: с двумя цехами одинаковые
                        имена в разных городах иначе не различить */}
                    {u.branch_name && branches.length > 1 && (
                      <span style={{
                        marginLeft: 8, background: 'var(--accent-weak)',
                        color: 'var(--accent)', borderRadius: 6,
                        padding: '1px 8px', fontWeight: 700,
                      }}>{u.branch_name}</span>
                    )}
                    {u.all_branches && (
                      <span style={{
                        marginLeft: 8, background: '#f59e0b18', color: '#f59e0b',
                        borderRadius: 6, padding: '1px 8px', fontWeight: 700,
                      }}>👁 все цеха</span>
                    )}
                    {!u.all_branches && (u.extra_branches || []).length > 0 && (
                      <span style={{
                        marginLeft: 8, background: '#f59e0b18', color: '#f59e0b',
                        borderRadius: 6, padding: '1px 8px', fontWeight: 700,
                      }}>👁 +{u.extra_branches.length}</span>
                    )}
                    {u.press_name && (
                      <span style={{
                        marginLeft: 8, background: '#0ea5e918', color: '#0ea5e9',
                        borderRadius: 6, padding: '1px 8px', fontWeight: 700,
                      }}>⚙ {u.press_name}</span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn size="sm" variant="ghost" onClick={() => openEdit(u)}>{t('✎ Изменить')}</Btn>
                <Btn size="sm" variant="danger" onClick={() => handleDeactivate(u)}
                  style={{ opacity: u.username === 'admin' ? 0.3 : 1 }}
                  disabled={u.username === 'admin'}>
                  Деакт.
                </Btn>
              </div>
            </div>
          ))}
        </div>
        </div>
        ))}
      </Card>

      {/* Неактивные пользователи */}
      {inactiveUsers.length > 0 && (
        <Card style={{ opacity: 0.7 }}>
          <p style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            ДЕАКТИВИРОВАННЫЕ ({inactiveUsers.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {inactiveUsers.map(u => (
              <div key={u.id} style={{
                background: 'var(--bg)', borderRadius: 8, padding: '10px 14px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ color: 'var(--dim)', fontSize: 13 }}>
                  @{u.username} — {ROLE_LABELS[u.role] || u.role}
                </span>
                <Btn size="sm" variant="success" onClick={() => handleReactivate(u)}>{t('Активировать')}</Btn>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Рецепты (перенесены из менеджера) ────────────────────────────────────────
function RecipesPanel() {
  const [recipes, setRecipes] = useState([]);
  const [rawMats, setRawMats] = useState([]);
  const [finProds, setFinProds] = useState([]);
  const [editId, setEditId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ product: '', items: [{ material: '', quantity: '' }] });
  const [saving, setSaving] = useState(false);

  const load = () => salesAPI.getRecipes().then(r => setRecipes(r.data));
  useEffect(() => {
    load();
    warehouseAPI.getRawMaterials().then(r => setRawMats(r.data));
    warehouseAPI.getFinishedProducts().then(r => setFinProds(r.data));
  }, []);

  const openCreate = () => {
    setEditId(null);
    setForm({ product: '', items: [{ material: '', quantity: '' }] });
    setShowCreate(true);
  };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      product: r.product,
      items: r.items.map(i => ({ material: i.material, quantity: i.quantity })),
    });
    setShowCreate(true);
  };

  const updateItem = (idx, field, val) =>
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [field]: val } : it) }));

  const handleSave = async () => {
    if (!form.product || form.items.some(i => !i.material || !i.quantity)) {
      toast.error(t("Заполните все поля")); return;
    }
    setSaving(true);
    try {
      const payload = { product: Number(form.product), is_active: true, items: form.items.map(i => ({ material: Number(i.material), quantity: i.quantity })) };
      if (editId) await salesAPI.updateRecipe(editId, payload);
      else await salesAPI.createRecipe(payload);
      toast.success(editId ? 'Рецепт обновлён' : 'Рецепт создан');
      setShowCreate(false);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Ошибка'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!await confirmToast('Удалить рецепт?', { danger: true, confirmText: 'Удалить' })) return;
    await salesAPI.deleteRecipe(id);
    toast.success(t("Удалено"));
    load();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700 }}>{t('📋 Рецепты производства')}</h2>
        <Btn onClick={openCreate}>{t('+ Новый рецепт')}</Btn>
      </div>

      {showCreate && (
        <Card style={{ border: '1px solid var(--border)' }}>
          <h3 style={{ color: 'var(--text)', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
            {editId ? 'Редактировать рецепт' : 'Новый рецепт'}
          </h3>
          <div style={{ marginBottom: 12 }}>
            <Sel label="Готовый продукт" value={form.product} onChange={e => setForm({ ...form, product: e.target.value })}>
              <option value="">{t('— выбрать ЛДСП —')}</option>
              {finProds.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Sel>
          </div>
          <p style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{t('ИНГРЕДИЕНТЫ (на 1 лист)')}</p>
          {form.items.map((it, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 2 }}>
                <Sel value={it.material} onChange={e => updateItem(idx, 'material', e.target.value)}>
                  <option value="">{t('— сырьё —')}</option>
                  {rawMats.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </Sel>
              </div>
              <div style={{ width: 110 }}>
                <Inp type="number" step="0.001" min={0} placeholder={t("Кол-во")}
                  value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} />
              </div>
              {form.items.length > 1 && (
                <button onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18 }}>🗑</button>
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <Btn variant="ghost" size="sm" onClick={() => setForm(f => ({ ...f, items: [...f.items, { material: '', quantity: '' }] }))}>
              + Ингредиент
            </Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить'}</Btn>
            <Btn variant="ghost" onClick={() => setShowCreate(false)}>{t('Отмена')}</Btn>
          </div>
        </Card>
      )}

      <Card>
        {recipes.length === 0 ? (
          <p style={{ color: 'var(--dim)', textAlign: 'center', padding: 20 }}>{t('Рецептов нет')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recipes.map(r => (
              <div key={r.id} style={{ background: 'var(--bg)', borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14 }}>{r.product_name}</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Btn size="sm" variant="ghost" onClick={() => openEdit(r)}>✎</Btn>
                    <Btn size="sm" variant="danger" onClick={() => handleDelete(r.id)}>🗑</Btn>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {r.items.map(i => (
                    <span key={i.id} style={{
                      background: 'var(--card)', border: '1px solid var(--border)',
                      borderRadius: 6, padding: '2px 10px', fontSize: 12, color: 'var(--muted)',
                    }}>
                      {i.material_name} × {i.quantity} {i.material_unit}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Нормы прессов ────────────────────────────────────────────────────────────
// Настройки живут на филиале — филиал один, но сохраняем во все, чтобы
// второй, когда он появится, не оказался с чужими правилами молча.
function useBranchSettings() {
  const [branches, setBranches] = useState([]);
  // Цех, чьи настройки правим сейчас.
  //
  // Здесь стояло «первый в списке», а сохранение уходило СРАЗУ ВО ВСЕ цеха.
  // Пока цех был один, это одно и то же. На трёх — администратор открывал
  // «Общие правила», видел значения Алматы и, передвинув один тумблер, менял
  // порядок работы Чимкента и Астаны заодно. Ни на экране, ни в тосте об этом
  // не было ни слова: настройка называется общей, а цеха у неё разные.
  const [curId, setCurId] = useState(null);

  const load = useCallback(() => {
    branchAPI.getList().then(r => {
      const list = r.data || [];
      setBranches(list);
      // По умолчанию — тот, на который человек смотрит переключателем вверху.
      // Он же стоит в заголовке страницы, а два разных ответа на вопрос «чей
      // это цех» на одном экране быть не должно.
      const picked = sessionStorage.getItem('selected_branch');
      const hit = list.find(b => String(b.id) === String(picked));
      setCurId(prev => (
        prev !== null && list.some(b => b.id === prev) ? prev
          : (hit ? hit.id : (list[0] ? list[0].id : null))));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const branch = branches.find(b => b.id === curId) || null;

  const save = useCallback(async (patch) => {
    if (!branch) return false;
    try {
      await branchAPI.update(branch.id, patch);
      setBranches(prev => prev.map(b => (b.id === branch.id ? { ...b, ...patch } : b)));
      // Цех называем прямо в тосте: настройка у каждого своя, и человек должен
      // видеть, чью он только что поменял.
      toast.success(`${t('Настройка сохранена')} — ${branch.name}`);
      return true;
    } catch {
      toast.error(t('Ошибка сохранения'));
      return false;
    }
  }, [branch]);

  return { branch, branches, curId, setCurId, save, setBranches };
}

// ─── Уборка тестовых данных ──────────────────────────────────────────────────
//
// Пока систему настраивали, через неё прогнали пробную работу: тестовый пресс,
// выдуманные контрагенты, номенклатура, по которой ничего не выпускалось. Всё
// это лежит вперемешку с настоящим — в отчётах, в остатках, в очереди. Часть
// остатков от этого в минусе: списание по тестовой смене настоящее, а прихода
// за ним нет.
//
// Удалять такое на глазок нельзя: за одной тестовой сменой стоят листы,
// движения склада, позиция очереди и заказ. Поэтому здесь два шага — сначала
// показываем, ЧТО исчезнет и что вернётся на склад, и только потом удаляем.
// Кнопку нажимает человек, а не программа.
function TestDataPanel() {
  const [data, setData]       = useState(null);
  const [clients, setClients] = useState([]);
  const [presses, setPresses] = useState([]);
  const [noms, setNoms]       = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [q, setQ]             = useState('');
  const [done, setDone]       = useState(null);
  // У справочника свой поиск: контрагентов полсотни, а позиций больше тысячи,
  // и одно поле на два списка сразу мешало бы обоим
  const [nq, setNq]           = useState('');
  const [nfilter, setNfilter] = useState('');  // '' | 'mine' | 'free' | 'stock'
  // Снимать материал с чужих рецептов — отдельное решение, не побочный эффект
  const [forceRec, setForceRec] = useState(false);
  const [forceMt, setForceMt]   = useState(false);
  // Три списка друг под другом давали ленту в несколько экранов: чтобы
  // добраться до справочника, приходилось прокручивать полсотни контрагентов.
  // Разбираются с ними всё равно по одному, поэтому — вкладки.
  const [tab, setTab] = useState('nom');

  const load = useCallback(() => {
    testDataAPI.list().then(r => setData(r.data)).catch(() => toast.error(t('Ошибка загрузки')));
  }, []);
  useEffect(() => { load(); }, [load]);

  const body = { clients, presses, nomenclatures: noms };
  const chosen = clients.length + presses.length + noms.length;

  const toggle = (arr, setArr, id) =>
    setArr(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);

  const doPreview = async () => {
    if (!chosen) { toast.error(t('Ничего не выбрано')); return; }
    setBusy(true);
    try {
      const { data: r } = await testDataAPI.preview(body);
      setPreview(r.preview);
      setDone(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setBusy(false); }
  };

  const doWipe = async () => {
    if (!preview) return;
    if (!await confirmToast(
      `Удалить безвозвратно: заказов ${preview.orders}, смен ${preview.jobs}, листов ${preview.sheets}?`,
      { confirmText: 'Удалить' })) return;
    setBusy(true);
    try {
      const { data: r } = await testDataAPI.wipe(
        { ...body, force: true, force_recipes: forceRec, force_transfers: forceMt });
      setDone(r.deleted);
      setPreview(null);
      setClients([]); setPresses([]); setNoms([]);
      setForceRec(false); setForceMt(false);
      // Отчитываемся по тому, что вышло, а не по тому, что нажали. Раньше тут
      // всегда стояло «удалены» — даже когда позиция осталась в справочнике,
      // и человек уходил уверенный, что убрал её.
      const gone = (r.deleted.orders || 0) + (r.deleted.jobs || 0) +
                   (r.deleted.sheets || 0) + (r.deleted.clients || 0) +
                   (r.deleted.nomenclatures || []).length;
      if (gone) toast.success(t('Удалено'));
      else toast(t('Ничего не удалено — смотрите, что мешает'), { icon: '\u26a0' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка удаления');
    } finally { setBusy(false); }
  };

  if (!data) return <div style={{ color: 'var(--dim)' }}>{t('Загрузка')}…</div>;

  const rowSt = (on) => ({
    display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px',
    borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit', width: '100%',
    textAlign: 'left', fontSize: 13.5,
    background: on ? '#ef444418' : 'var(--bg)',
    border: '1px solid ' + (on ? '#ef444470' : 'var(--border)'),
    color: 'var(--text)',
  });

  const visibleClients = (data.clients || []).filter(c =>
    makeMatcher(q)(c.name, c.inn_bin, c.phone));

  const nomAll = data.nomenclatures || [];
  const nomShown = nomAll.filter(n => {
    if (nfilter === 'mine'  && n.in_1c) return false;
    if (nfilter === 'free'  && !n.can_delete) return false;
    if (nfilter === 'stock' && n.balance === 0) return false;
    const s2 = nq.trim().toLowerCase();
    if (!s2) return true;
    return makeMatcher(s2)(n.name, n.category, n.code);
  });

  const TABS = [
    ['press',  '\u2699\ufe0f', 'Прессы',       (data.presses || []).length,  presses.length],
    ['client', '\ud83d\udc65', 'Контрагенты',  (data.clients || []).length,  clients.length],
    ['nom',    '\ud83d\udce6', 'Номенклатура', nomAll.length,                noms.length],
  ];

  // Карточка списка тянется до низа экрана. Раньше у неё стоял потолок в
  // 340 точек: под коротким списком оставалось пустое поле в половину
  // экрана, а длинный прокручивался в узком окошке, хотя место было.
  const fill = { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 12,
      // 58 — шапка страницы, 24+28 — поля основной области, ~54 — «к главному»
      height: 'calc(100vh - 172px)', minHeight: 430,
    }}>
      {/* Шапка. Прилипает: отбор идёт в длинном списке, а кнопка нужна из
          любого его места — иначе после отметки надо прокручивать обратно.
          Отметки живут по всем трём вкладкам сразу, поэтому счётчик общий. */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg)',
        padding: '4px 0 10px', display: 'flex', gap: 8, alignItems: 'center',
        flexWrap: 'wrap', borderBottom: '1px solid var(--border)',
      }}>
        {TABS.map(([k, icon, label, total, marked]) => (
          <button key={k} onClick={() => setTab(k)} className="touch-btn" style={{
            padding: '8px 14px', borderRadius: 9, fontSize: 13.5, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
            background: tab === k ? 'var(--accent)' : 'var(--card)',
            border: '1px solid ' + (tab === k ? 'var(--accent)' : 'var(--border)'),
            color: tab === k ? '#fff' : 'var(--muted)',
          }}>
            {icon} {t(label)}
            <span style={{ opacity: 0.7, fontWeight: 500 }}> {total}</span>
            {marked > 0 && (
              <span style={{
                marginLeft: 7, background: '#ef4444', color: '#fff',
                borderRadius: 20, padding: '1px 7px', fontSize: 11.5, fontWeight: 800,
              }}>{marked}</span>
            )}
          </button>
        ))}

        <span style={{ flex: 1 }} />

        {chosen > 0 && (
          <button onClick={() => { setClients([]); setPresses([]); setNoms([]); setPreview(null); }}
            style={{ background: 'none', border: 'none', color: 'var(--dim)',
                     cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
            {t('снять отметки')}
          </button>
        )}
        <Btn variant="primary" onClick={doPreview} disabled={busy || !chosen}>
          {busy ? t('Считаю…') : `${t('Что исчезнет')} (${chosen})`}
        </Btn>
      </div>

      {/* Тестовый пресс */}
      {tab === 'press' && (
      <Card style={{ ...fill, overflowY: 'auto' }}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>⚙️ {t('Работа тестового пресса')}</div>
        {(data.presses || []).map(p => (
          <button key={p.id} onClick={() => toggle(presses, setPresses, p.id)}
            style={{ ...rowSt(presses.includes(p.id)), marginBottom: 6 }}>
            <span style={{ fontSize: 16 }}>{presses.includes(p.id) ? '☑' : '☐'}</span>
            <span style={{ flex: 1 }}>
              <b>{p.name}</b>
              <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 2 }}>
                смен {p.jobs} · листов {p.sheets} (на складе {p.in_stock})
                {p.orders.length > 0 && <> · заказы: {p.orders.join(', ')}</>}
              </div>
            </span>
          </button>
        ))}
        {(data.presses || []).length === 0 && (
          <div style={{ color: 'var(--dim)', fontSize: 13 }}>
            {t('Прессов с «тест» в названии нет')}
          </div>
        )}
      </Card>
      )}

      {/* Контрагенты */}
      {tab === 'client' && (
      <Card style={fill}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{ fontWeight: 700 }}>👥 {t('Контрагенты и их заказы')}</div>
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder={t('Поиск…')}
            style={{
              marginLeft: 'auto', width: 200, padding: '6px 10px',
              background: 'var(--input)', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none',
            }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6,
                      flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {visibleClients.map(c => (
            <button key={c.id} onClick={() => toggle(clients, setClients, c.id)}
              style={rowSt(clients.includes(c.id))}>
              <span style={{ fontSize: 16 }}>{clients.includes(c.id) ? '☑' : '☐'}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <b>{c.name}</b>
                <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 2 }}>
                  {c.orders === 0 && c.sheets === 0
                    ? 'пустая карточка'
                    : <>заказов {c.orders} · листов {c.sheets} (на складе {c.in_stock}
                        {c.shipped > 0 && <>, отгружено {c.shipped}</>})</>}
                  {c.numbers.length > 0 && <> · {c.numbers.join(', ')}</>}
                </div>
              </span>
            </button>
          ))}
        </div>
      </Card>
      )}

      {tab === 'nom' && (
      <>
      {/* Номенклатура.
          Здесь стоял фильтр по названию — показывались только позиции,
          начинающиеся на «OQ». Пробу могли завести под любым именем, и пять
          строк из тысячи с лишним выглядели так, будто справочник чист.
          Теперь виден весь справочник, а отбирают в нём поиском. */}
      <Card style={fill}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                      marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700 }}>{'\ud83d\udce6'} {t('Номенклатура')}</div>
          <input value={nq} onChange={e => setNq(e.target.value)}
            placeholder={t('Название или категория…')}
            style={{
              flex: '1 1 220px', minWidth: 180, padding: '7px 11px',
              background: 'var(--input)', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none',
              fontFamily: 'inherit',
            }} />
        </div>

        {/* Отбор. «Нет в 1С» — те, за кем ещё не закреплён GUID: заведены у
            нас и наверх не ушли. Позиция, уже попавшая в 1С, ничем не
            отличается от пришедших оттуда, и убирать её надо в обеих
            системах, а не только здесь. */}
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
          {[['', 'Все', nomAll.length],
            ['mine', 'Нет в 1С', nomAll.filter(n => !n.in_1c).length],
            ['free', 'Ничем не заняты', nomAll.filter(n => n.can_delete).length],
            ['stock', 'Есть остаток', nomAll.filter(n => n.balance !== 0).length],
           ].map(([k, label, cnt]) => (
            <button key={k || 'all'} onClick={() => setNfilter(k)} className="touch-btn"
              style={{
                padding: '6px 12px', borderRadius: 18, fontSize: 12.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                background: nfilter === k ? 'var(--accent)' : 'var(--bg)',
                border: '1px solid ' + (nfilter === k ? 'var(--accent)' : 'var(--border)'),
                color: nfilter === k ? '#fff' : 'var(--muted)',
              }}>{t(label)} ({cnt})</button>
          ))}
        </div>

        {/* Отметить всё найденное. Отбор дал сорок позиций — щёлкать их по
            одной человек не станет, а значит и уборку отложит. */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center',
                      marginBottom: 8, fontSize: 12.5, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--dim)' }}>
            {t('найдено')} {nomShown.length}
            {noms.length > 0 && <> · {t('отмечено')} {noms.length}</>}
          </span>
          <button onClick={() => setNoms([...new Set([...noms, ...nomShown.map(n => n.id)])])}
            style={{ background: 'none', border: 'none', color: 'var(--accent)',
                     cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit',
                     fontWeight: 700, padding: 0 }}>
            {'\u2611'} {t('отметить всё найденное')}
          </button>
          {noms.length > 0 && (
            <button onClick={() => setNoms([])}
              style={{ background: 'none', border: 'none', color: 'var(--dim)',
                       cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit', padding: 0 }}>
              {t('снять отметки')}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5,
                      flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {nomShown.slice(0, 300).map(n => (
            <button key={n.id} onClick={() => toggle(noms, setNoms, n.id)}
              style={rowSt(noms.includes(n.id))}>
              <span style={{ fontSize: 16 }}>{noms.includes(n.id) ? '\u2611' : '\u2610'}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <b>{n.name}</b>
                {!n.in_1c && (
                  <span style={{ marginLeft: 7, background: '#f59e0b22', color: '#f59e0b',
                                 borderRadius: 5, padding: '1px 7px', fontSize: 11,
                                 fontWeight: 700 }}>{t('нет в 1С')}</span>
                )}
                <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 2 }}>
                  {n.category} · остаток {n.balance} {n.unit}
                  {n.clears.length > 0 && <> · {n.clears.join(' · ')}</>}
                </div>
                {/* Что не пускает. Без этого «осталась в справочнике» —
                    тупик: непонятно, куда идти дальше. */}
                {n.holds.length > 0 && (
                  <div style={{ color: '#f59e0b', fontSize: 12, marginTop: 2 }}>
                    {'\u26a0'} занята: {n.holds.join(' · ')}
                  </div>
                )}
              </span>
            </button>
          ))}
          {nomShown.length > 300 && (
            <div style={{ color: 'var(--dim)', fontSize: 12, padding: '8px 12px' }}>
              показаны первые 300 из {nomShown.length} — уточните поиск
            </div>
          )}
          {nomShown.length === 0 && (
            <div style={{ color: 'var(--dim)', fontSize: 13, padding: 12 }}>
              {t('Ничего не найдено')}
            </div>
          )}
        </div>
      </Card>
      </>
      )}

      {/* Что исчезнет. Раньше это разворачивалось четвёртым блоком под
          списками — читать итог приходилось, прокрутив мимо всего, что
          выбирал. Решение принимают по нему одному, поэтому оно и
          показывается одно. */}
      {preview && (
        <div onClick={() => setPreview(null)} style={{
          position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
        <Card onClick={e => e.stopPropagation()} style={{
          borderColor: '#ef4444', border: '1px solid #ef4444',
          maxWidth: 820, width: '100%', maxHeight: '88vh', overflowY: 'auto',
        }}>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 12, color: '#ef4444' }}>
            ⚠ {t('Исчезнет безвозвратно')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                        gap: 12, marginBottom: 14 }}>
            {[['Заказов', preview.orders], ['Позиций', preview.order_items],
              ['Смен пресса', preview.jobs], ['Листов', preview.sheets],
              ['Из них на складе', preview.sheets_in_stock],
              ['Движений склада', preview.stock_entries]].map(([k, v]) => (
              <div key={k}>
                <div style={{ fontSize: 11, color: 'var(--dim)', fontWeight: 700,
                              textTransform: 'uppercase' }}>{t(k)}</div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{v}</div>
              </div>
            ))}
          </div>

          {preview.stock.length > 0 && (
            <>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                📦 {t('Остаток изменится так')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
                {preview.stock.map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, fontSize: 13 }}>
                    <span style={{ flex: 1, color: 'var(--muted)', minWidth: 0 }}>{r.name}</span>
                    <b style={{ color: r.delta > 0 ? '#22c55e' : '#ef4444', whiteSpace: 'nowrap' }}>
                      {r.delta > 0 ? '+' : ''}{r.delta} {r.unit}
                    </b>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Справочник. Общее число тут бесполезно: важно, какая позиция
              уйдёт, а какая останется и из-за чего именно. Пока материал
              стоит в рецепте чужого изделия, убрать его нельзя — сначала
              технолог заменяет его там, иначе изделие останется без
              списания сырья, и заметят это через месяц в отчёте. */}
          {(preview.nomenclatures || []).length > 0 && (
            <>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                {'\ud83d\udcd0'} {t('Справочник')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6,
                            marginBottom: 14 }}>
                {preview.nomenclatures.map(n => (
                  <div key={n.id} style={{ fontSize: 13 }}>
                    <span style={{ color: n.can_delete ? '#ef4444' : 'var(--muted)' }}>
                      {n.can_delete ? '\u2716' : '\u26a0'} {n.name}
                    </span>
                    <span style={{ color: 'var(--dim)' }}>
                      {' — '}
                      {n.can_delete
                        ? 'удалится, вместе с ней движений ' + n.entries +
                          ' и остаток ' + n.balance + ' ' + n.unit
                        : 'останется: ' + n.holds.join('; ')}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {preview.blockers.length > 0 && (
            <div style={{ background: '#f59e0b18', border: '1px solid #f59e0b55',
                          borderRadius: 9, padding: '10px 13px', fontSize: 13, marginBottom: 14 }}>
              {preview.blockers.map((b, i) => <div key={i}>⚠ {b}</div>)}
            </div>
          )}

          {/* Материал стоит в рецептах других изделий. Убрать его оттуда —
              отдельное решение: изделие без строки сырья выпускается молча и
              ничего не списывает, а заметят это в отчёте через месяц.
              Поэтому перечисляем изделия поимённо и требуем отдельной
              отметки, а не прячем это внутрь кнопки. */}
          {(preview.recipes_losing || []).length > 0 && (
            <div style={{ background: '#ef444412', border: '1px solid #ef444455',
                          borderRadius: 9, padding: '11px 13px', marginBottom: 14 }}>
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
                              cursor: 'pointer', fontSize: 13.5 }}>
                <input type="checkbox" checked={forceRec}
                  onChange={e => setForceRec(e.target.checked)}
                  style={{ width: 18, height: 18, marginTop: 1, flexShrink: 0 }} />
                <span>
                  <b>{t('Убрать и из рецептов других изделий')}</b>
                  <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 4,
                                lineHeight: 1.6 }}>
                    Без этого позиция останется в справочнике. С этим — у изделий
                    ниже пропадёт строка материала, и до нового рецепта они
                    будут выпускаться без списания сырья.
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column',
                                gap: 3, maxHeight: 170, overflowY: 'auto' }}>
                    {preview.recipes_losing.map((r, i) => (
                      <div key={i} style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                        • {r.product}
                        <span style={{ color: 'var(--dim)' }}> — {r.materials.join(', ')}</span>
                      </div>
                    ))}
                  </div>
                </span>
              </label>
            </div>
          )}

          {/* Передачи материала. Это документ цеха: столько-то листов ушло из
              производства на склад. Пробную от настоящей отличает только
              человек, поэтому — отдельная отметка и список с датами. */}
          {(preview.transfers_losing || []).length > 0 && (
            <div style={{ background: '#f59e0b12', border: '1px solid #f59e0b55',
                          borderRadius: 9, padding: '11px 13px', marginBottom: 14 }}>
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
                              cursor: 'pointer', fontSize: 13.5 }}>
                <input type="checkbox" checked={forceMt}
                  onChange={e => setForceMt(e.target.checked)}
                  style={{ width: 18, height: 18, marginTop: 1, flexShrink: 0 }} />
                <span>
                  <b>{t('Удалить и передачи материала')}</b>
                  <div style={{ marginTop: 7, display: 'flex', flexDirection: 'column',
                                gap: 3, maxHeight: 150, overflowY: 'auto' }}>
                    {preview.transfers_losing.map(m => (
                      <div key={m.id} style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                        • {m.when} · {m.name} · {m.quantity}
                        <span style={{ color: 'var(--dim)' }}> · {m.who} · {m.status}</span>
                      </div>
                    ))}
                  </div>
                </span>
              </label>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Btn variant="danger" onClick={doWipe} disabled={busy}>
              {'\ud83d\uddd1'} {t('Удалить безвозвратно')}
            </Btn>
            <button onClick={() => setPreview(null)}
              style={{ background: 'none', border: 'none', color: 'var(--dim)',
                       cursor: 'pointer', fontSize: 13.5, fontFamily: 'inherit' }}>
              {t('Отмена')}
            </button>
          </div>
        </Card>
        </div>
      )}

      {/* Что удалено */}
      {done && (
        <div onClick={() => setDone(null)} style={{
          position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
        <Card onClick={e => e.stopPropagation()} style={{
          border: '1px solid #22c55e', maxWidth: 700, width: '100%',
          maxHeight: '88vh', overflowY: 'auto',
        }}>
          <div style={{ fontWeight: 800, color: '#22c55e', marginBottom: 10 }}>
            ✅ {t('Удалено')}
          </div>
          <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.7 }}>
            заказов {done.orders || 0} · позиций {done.order_items || 0} ·
            смен {done.jobs || 0} · листов {done.sheets || 0} ·
            движений склада {done.stock_entries || 0} ·
            контрагентов {done.clients || 0}
            {(done.nomenclatures || []).length > 0 && (
              <div>номенклатура удалена: {done.nomenclatures.join(', ')}</div>
            )}
            {/* На позицию могут ссылаться приходы и передачи материала —
                удалить её база не даст, но остальное уже убрано */}
            {(done.nomenclatures_kept || []).length > 0 && (
              <div style={{ color: '#f59e0b', marginTop: 6 }}>
                осталась в справочнике: {done.nomenclatures_kept.join(' · ')}
              </div>
            )}
          </div>
          <div style={{ marginTop: 14 }}>
            <Btn onClick={() => setDone(null)}>{t('Понятно')}</Btn>
          </div>
        </Card>
        </div>
      )}
    </div>
  );
}

// ─── Общие настройки ─────────────────────────────────────────────────────────
function GeneralSettingsPanel() {
  const { branch, branches, curId, setCurId, save, setBranches } = useBranchSettings();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700 }}>
        ⚙️ {t('Настройки системы')}
      </h2>

      {/* Правила у каждого цеха свои: в Алматы принтер бирок есть, в Чимкенте
          нет. Пока цех был один, спрашивать было не о чем — и экран молчал,
          а сохранял во все. Раз цехов несколько, он обязан назвать, чей
          именно порядок работы сейчас меняют. */}
      {branches.length > 1 && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--muted)', fontSize: 13.5, fontWeight: 700 }}>
              {t('Правила какого цеха')}:
            </span>
            {branches.map(b => (
              <button key={b.id} onClick={() => setCurId(b.id)}
                className="touch-btn" style={{
                  background: b.id === curId ? 'var(--accent)' : 'var(--bg)',
                  color: b.id === curId ? '#fff' : 'var(--muted)',
                  border: '1px solid var(--border)', borderRadius: 9,
                  padding: '7px 16px', fontSize: 13.5,
                  fontWeight: b.id === curId ? 800 : 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>{b.name}</button>
            ))}
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 8 }}>
            Тумблеры ниже относятся только к выбранному цеху. Соседние остаются
            как были.
          </div>
        </Card>
      )}
      <Card>
        {!branch && <div style={{ color: 'var(--dim)', fontSize: 14 }}>{t('Загрузка')}…</div>}
        {branch && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '6px 0' }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
                Принимать заказы, когда сырья не хватает
              </div>
              <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 3, maxWidth: 620 }}>
                Заказ берётся, но на нём остаётся предупреждение: чего и сколько
                не хватает. Технолог и менеджер видят его до того, как заказ
                дойдёт до пресса. Выключено — заказ без сырья не принимается вовсе.
              </div>
            </div>
            <button
              onClick={() => save({ allow_order_without_stock: !branch.allow_order_without_stock })}
              style={{
                width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                background: branch.allow_order_without_stock ? '#22c55e' : 'var(--border)',
                position: 'relative', transition: 'background .2s', flexShrink: 0,
              }}>
              <div style={{
                width: 20, height: 20, background: '#fff', borderRadius: '50%',
                position: 'absolute', top: 3, left: branch.allow_order_without_stock ? 23 : 3,
                transition: 'left .2s', boxShadow: '0 2px 4px rgba(0,0,0,.2)',
              }} />
            </button>
          </div>
        )}
      </Card>

      {/* Сорт листу назначают по бирке — приёмщик подносит сканер к листу.
          Но печатать бирки в цеху бывает нечем: принтера нет и поставить
          некуда. Тогда сортировка не работает вовсе — сорт остаётся высшим
          у всей смены, включая брак, и склад отдаёт брак как годный. */}
      <Card>
        {branch && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '6px 0' }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
                Менять сорт без бирки, по количеству
              </div>
              <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 3, maxWidth: 620 }}>
                Приёмщик пишет числа: второго сорта столько-то, брака
                столько-то — система сама берёт нужное количество листов.
                Какой именно лист второго сорта, при этом не знает никто: без
                бирки лист обезличен. Выключено — сорт ставится только
                сканированием бирки, как раньше.
              </div>
            </div>
            <button
              onClick={() => save({ grade_without_label: !branch.grade_without_label })}
              style={{
                width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                background: branch.grade_without_label ? '#22c55e' : 'var(--border)',
                position: 'relative', transition: 'background .2s', flexShrink: 0,
              }}>
              <div style={{
                width: 20, height: 20, background: '#fff', borderRadius: '50%',
                position: 'absolute', top: 3, left: branch.grade_without_label ? 23 : 3,
                transition: 'left .2s', boxShadow: '0 2px 4px rgba(0,0,0,.2)',
              }} />
            </button>
          </div>
        )}

        {/* Печать бирок. Выключено, пока принтера нет: кнопка, которая ничего
            не печатает, только сбивает с толку. Приёмка от неё не зависит —
            стопку принимают по числу, а не по биркам. */}
        {branch && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0 6px',
            borderTop: '1px solid var(--border)', marginTop: 12,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
                Печать бирок на складе
              </div>
              <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 3, maxWidth: 620 }}>
                В окне приёмки появится кнопка «Бирки на добавленные листы».
                Печатает только тем, кому бирку у пресса не печатали: склад
                досчитал доски, которых пресс не отметил, — коды у них есть, а
                бумаги нет. На остальных листах бирки наклеены ещё в цеху, и
                вся стопка не печатается. Нет принтера — держите выключенной.
              </div>
            </div>
            <button
              onClick={() => save({ label_print: !branch.label_print })}
              style={{
                width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                background: branch.label_print ? '#22c55e' : 'var(--border)',
                position: 'relative', transition: 'background .2s', flexShrink: 0,
              }}>
              <div style={{
                width: 20, height: 20, background: '#fff', borderRadius: '50%',
                position: 'absolute', top: 3, left: branch.label_print ? 23 : 3,
                transition: 'left .2s', boxShadow: '0 2px 4px rgba(0,0,0,.2)',
              }} />
            </button>
          </div>
        )}

        {branch && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 16,
            padding: '14px 0', borderTop: '1px solid var(--border)',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
                Строгий порядок заданий у пресса
              </div>
              {/* Порядок в смене не произвольный: сначала тёмное, потом
                  светлое — грязь на прессе не видна на тёмном, а к светлым
                  его успевают протереть; внутри цвета от холодного режима к
                  горячему — нагреть быстро, остыть плита сама не спешит.
                  Технолог задаёт этот порядок в заявке, а оператор до сих
                  пор брал из очереди что придётся.

                  Выключено по умолчанию не из осторожности: тон цвета и
                  эталонная температура проставлены пока не у всех ЛДСП, и
                  запрет по неполным данным остановил бы цех на ровном
                  месте. */}
              <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 3, maxWidth: 620 }}>
                Оператор сможет начать только ту позицию, которая стоит в
                очереди первой. Порядок задаёт технолог в заявке: сначала
                тёмное, потом светлое, внутри цвета — от холодного режима к
                горячему.
                <b style={{ color: '#f59e0b' }}> Включайте, только когда тон
                цвета и режим проставлены у всех ЛДСП</b> — иначе пресс
                встанет из-за незаполненной карточки.
              </div>
            </div>
            <button
              onClick={() => save({ enforce_queue_order: !branch.enforce_queue_order })}
              style={{
                width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                background: branch.enforce_queue_order ? '#22c55e' : 'var(--border)',
                position: 'relative', transition: 'background .2s', flexShrink: 0,
              }}>
              <div style={{
                width: 20, height: 20, background: '#fff', borderRadius: '50%',
                position: 'absolute', top: 3, left: branch.enforce_queue_order ? 23 : 3,
                transition: 'left .2s', boxShadow: '0 2px 4px rgba(0,0,0,.2)',
              }} />
            </button>
          </div>
        )}

        {/* Завод плиты и плёнки у пресса: спрашивать обязательно или нет.
            По цеху отдельно — в одном учёт по заводам уже ведут, в другом
            справочник ещё пустой, и обязательное поле там просто не дало бы
            начать смену. */}
        {branch && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 16,
            padding: '14px 0', borderTop: '1px solid var(--border)',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
                Требовать завод плиты и плёнки при старте смены
              </div>
              <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 3, maxWidth: 620 }}>
                Оператор выбирает завод из справочника «Заводы-изготовители»
                рядом с партией плиты и номером паллеты. Без этого разбор брака
                не отвечает на вопрос «чья была плита».
                <b style={{ color: '#f59e0b' }}> Включайте, когда справочник
                заполнен</b> — иначе смену не начать.
              </div>
            </div>
            <button
              onClick={() => save({ require_material_maker: !branch.require_material_maker })}
              style={{
                width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                background: branch.require_material_maker ? '#22c55e' : 'var(--border)',
                position: 'relative', transition: 'background .2s', flexShrink: 0,
              }}>
              <div style={{
                width: 20, height: 20, background: '#fff', borderRadius: '50%',
                position: 'absolute', top: 3, left: branch.require_material_maker ? 23 : 3,
                transition: 'left .2s', boxShadow: '0 2px 4px rgba(0,0,0,.2)',
              }} />
            </button>
          </div>
        )}

        {branch && <PassportPasswordRow branch={branch} setBranches={setBranches} />}
      </Card>

      <TabloKeysCard />
      <TelegramCard />
      <PublicContactCard />
    </div>
  );
}

// ─── Пароль к закрытой части паспорта изделия ────────────────────────────────
//
// Паспорт (/S/<код>) открывает любой, кто отсканировал бирку. Заказчика, режим
// прессования и партии материала завод попросил закрыть: свои видят их после
// входа в MES, остальным — этот пароль. Сам пароль сервер назад не отдаёт,
// только «задан ли»: забыли — задают новый.
function PassportPasswordRow({ branch, setBranches }) {
  const [val, setVal]   = useState('');
  const [busy, setBusy] = useState(false);

  const put = async (raw) => {
    setBusy(true);
    try {
      const r = await branchAPI.update(branch.id, { passport_password: raw });
      const isSet = !!r.data?.passport_password_set;
      setBranches(prev => prev.map(b => (b.id === branch.id
        ? { ...b, passport_password_set: isSet } : b)));
      setVal('');
      toast.success(raw ? `Пароль паспорта задан — ${branch.name}`
                        : `Пароль паспорта снят — ${branch.name}`);
    } catch (e) {
      toast.error(e.response?.data?.error || t('Ошибка сохранения'));
    } finally { setBusy(false); }
  };

  return (
    <div style={{ padding: '14px 0', borderTop: '1px solid var(--border)' }}>
      <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
        Пароль к закрытой части паспорта изделия
        <span style={{
          marginLeft: 10, fontSize: 12, fontWeight: 600,
          color: branch.passport_password_set ? '#22c55e' : 'var(--dim)',
        }}>{branch.passport_password_set ? '● задан' : '○ не задан'}</span>
      </div>
      <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 3, maxWidth: 620 }}>
        По QR-коду все видят сорт, пресс, изделие и номер заказа. Заказчик,
        режим прессования, партии и заводы плиты и плёнки открываются
        сотрудникам после входа в MES, а остальным — по этому паролю.
        Не задан — без входа их не увидит никто.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <input type="password" value={val} autoComplete="new-password"
          onChange={e => setVal(e.target.value)}
          placeholder={branch.passport_password_set ? 'Новый пароль' : 'Пароль'}
          style={{ flex: '1 1 200px', maxWidth: 280, padding: '8px 12px', background: 'var(--input)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14 }} />
        <button disabled={busy || val.trim().length < 4} onClick={() => put(val.trim())}
          style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (busy || val.trim().length < 4) ? 0.5 : 1 }}>
          Сохранить
        </button>
        {branch.passport_password_set && (
          <button disabled={busy} onClick={() => put('')}
            style={{ background: 'transparent', color: 'var(--danger)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', fontSize: 14, cursor: 'pointer' }}>
            Снять пароль
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Бот уведомлений ─────────────────────────────────────────────────────────
//
// Клиент не должен сидеть у экрана и обновлять страницу: он подписывается на
// свой заказ одним нажатием и получает сообщение, когда тот готов.
//
// Бот ОДИН на весь завод, а не на цех: клиент не знает, в каком цехе делают
// его заказ, и знать не должен — для него это одна компания. Второй токен
// означал бы второе имя бота в переписке того же клиента.
function TelegramCard() {
  const [st, setSt]     = useState(null);
  const [token, setTok] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    salesAPI.getTelegram().then(r => setSt(r.data)).catch(() => setSt({}));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await salesAPI.saveTelegram(token.trim());
      setTok('');
      load();
      toast.success(`Бот @${r.data.bot_username} подключён`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Не удалось подключить');
    } finally { setBusy(false); }
  };

  const off = async () => {
    if (!await confirmToast(
      'Выключить уведомления? Клиенты перестанут получать сообщения о своих заказах.',
      { confirmText: 'Выключить' })) return;
    try { await salesAPI.disableTelegram(); load(); }
    catch { toast.error(t('Ошибка')); }
  };

  return (
    <Card>
      <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
        ✈️ Уведомления клиентам в Telegram
      </div>
      <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 3, maxWidth: 620,
                    lineHeight: 1.5 }}>
        Клиент нажимает ссылку в сообщении от менеджера, подписывается на свой
        заказ — и завод сам пишет ему, когда заказ взяли в работу, изготовили и
        отгрузили. Бот один на все цеха.
      </div>

      {st === null && (
        <div style={{ color: 'var(--dim)', fontSize: 13, marginTop: 12 }}>{t('Загрузка')}…</div>
      )}

      {st && st.is_active && (
        <div style={{
          background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
          padding: '10px 12px', marginTop: 12, display: 'flex', alignItems: 'center',
          gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ color: '#22c55e', fontSize: 13.5, fontWeight: 700 }}>
            ● включён
          </span>
          <span style={{ color: 'var(--text)', fontSize: 13.5 }}>
            @{st.bot_username}
          </span>
          <span style={{ color: 'var(--dim)', fontSize: 12 }}>
            подписок: {st.subscribers}
          </span>
          <button onClick={off} style={{
            marginLeft: 'auto', background: 'none', border: '1px solid var(--border)',
            borderRadius: 6, color: 'var(--danger)', padding: '6px 12px', fontSize: 12,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>{t('Выключить')}</button>
        </div>
      )}

      {st && !st.is_active && (
        <>
          {/* Токен вводят один раз. Проверка и включение — одна кнопка:
              «сохранил, но не включил» выглядит как рабочая настройка и
              при этом молчит. */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <input
              value={token}
              onChange={e => setTok(e.target.value)}
              placeholder="Токен от @BotFather — 123456789:AAG..."
              style={{
                flex: 1, minWidth: 260, background: 'var(--input)',
                border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
                padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none',
              }}
            />
            <button onClick={save} disabled={busy || !token.trim()} style={{
              background: 'var(--accent)', border: 'none', borderRadius: 8, color: '#fff',
              padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', opacity: (busy || !token.trim()) ? 0.6 : 1,
            }}>
              {t('Проверить и включить')}
            </button>
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 8, lineHeight: 1.6 }}>
            Как получить токен: в Telegram напишите <b>@BotFather</b> → команда
            <b> /newbot</b> → придумайте имя и адрес бота → он пришлёт строку
            вида <code>123456789:AAG…</code>. Вставьте её сюда.
          </div>
          {st.last_error && (
            <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 8 }}>
              Прошлая попытка: {st.last_error}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ─── Контакты для клиента ────────────────────────────────────────────────────
//
// На экране «где мой заказ» человек видит статус — и упирается в тупик:
// спросить не у кого. Номер менеджера он записал полгода назад в другом
// телефоне, а на странице заказа ни имени, ни телефона нет и не будет.
//
// Поэтому один общий номер завода. Личный номер менеджера сюда не годится:
// люди меняются, а ссылка у клиента остаётся жить своей жизнью.
//
// Правим КАЖДЫЙ цех отдельно, а не разом: у соседнего цеха свой адрес, и
// разослать один на всех — значит отправить клиента не туда.
function PublicContactCard() {
  const { branches, setBranches } = useBranchSettings();
  const [busy, setBusy] = useState(null);

  const put = (id, patch) => {
    setBranches(prev => prev.map(b => (b.id === id ? { ...b, ...patch } : b)));
  };

  const save = async (b) => {
    setBusy(b.id);
    try {
      await branchAPI.update(b.id, {
        public_phone: b.public_phone || '',
        address: b.address || '',
      });
      toast.success(t('Сохранено'));
    } catch { toast.error(t('Ошибка сохранения')); }
    finally { setBusy(null); }
  };

  const field = {
    background: 'var(--input)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text)', padding: '9px 12px', fontSize: 13, fontFamily: 'inherit',
    outline: 'none', width: '100%',
  };

  return (
    <Card>
      <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
        📞 Контакты для клиентов
      </div>
      <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 3, maxWidth: 620,
                    lineHeight: 1.5 }}>
        Телефон и адрес видны клиенту на экране его заказа — это единственный
        способ задать вопрос, не разыскивая менеджера. Ставьте общий номер
        завода, а не личный номер сотрудника: люди меняются, а ссылка у клиента
        остаётся.
      </div>

      {(branches || []).map(b => (
        <div key={b.id} style={{
          background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
          padding: '10px 12px', marginTop: 10,
        }}>
          <div style={{ color: 'var(--text)', fontSize: 13.5, fontWeight: 700,
                        marginBottom: 8 }}>
            {b.name}{b.city ? ` · ${b.city}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              value={b.public_phone || ''}
              onChange={e => put(b.id, { public_phone: e.target.value })}
              placeholder="+7 700 000 00 00"
              style={{ ...field, width: 200 }}
            />
            <input
              value={b.address || ''}
              onChange={e => put(b.id, { address: e.target.value })}
              placeholder="Адрес склада — куда приезжать за товаром"
              style={{ ...field, flex: 1, minWidth: 220 }}
            />
            <button onClick={() => save(b)} disabled={busy === b.id} style={{
              background: 'var(--accent)', border: 'none', borderRadius: 8, color: '#fff',
              padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', opacity: busy === b.id ? 0.6 : 1,
            }}>
              {t('Сохранить')}
            </button>
          </div>
        </div>
      ))}
    </Card>
  );
}

// ─── Табло в офисе ───────────────────────────────────────────────────────────
//
// Телевизор на стене показывает заказы в работе: номер, шаг, готовность.
// Ни фамилий, ни телефонов, ни сумм там нет — рядом с экраном ходят
// посторонние, и это единственный способ ничего не показать им лишнего.
//
// Логина у телевизора нет намеренно: учётка, оставленная на экране без
// присмотра, рано или поздно окажется чужой, а срок токена кончится ночью — и
// утром вместо табло будет окно входа. Экран открывают ссылкой с ключом.
// Ключ отзывается удалением строки: удалили — экран погас сразу.
function TabloKeysCard() {
  const [rows, setRows] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    salesAPI.getTabloKeys().then(r => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const linkOf = (row) => `${window.location.origin}/tablo?k=${row.token}`;

  const add = async () => {
    setBusy(true);
    try {
      await salesAPI.createTabloKey({ name: name.trim() });
      setName('');
      load();
      toast.success(t('Экран добавлен'));
    } catch { toast.error(t('Ошибка')); }
    finally { setBusy(false); }
  };

  const drop = async (row) => {
    if (!await confirmToast(
      `Отозвать ключ «${row.name || 'без названия'}»? Экран, открытый по этой ссылке, погаснет.`,
      { confirmText: 'Отозвать' })) return;
    try {
      await salesAPI.deleteTabloKey(row.id);
      load();
    } catch { toast.error(t('Ошибка')); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text)
      .then(() => toast.success(t('Ссылка скопирована')))
      .catch(() => toast.error(t('Скопируйте вручную')));
  };

  const seen = (iso) => iso
    ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit',
        hour: '2-digit', minute: '2-digit' })
    : 'ни разу';

  return (
    <Card>
      <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
        📺 Табло в офисе
      </div>
      <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 3, maxWidth: 620,
                    lineHeight: 1.5 }}>
        Экран с заказами в работе для телевизора. Откройте ссылку на том
        устройстве, где висит экран, — логин там не нужен. На табло видны
        только номер заказа и готовность: ни клиента, ни телефона, ни суммы.
      </div>

      {rows === null && (
        <div style={{ color: 'var(--dim)', fontSize: 13, marginTop: 12 }}>{t('Загрузка')}…</div>
      )}

      {(rows || []).map(row => (
        <div key={row.id} style={{
          background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
          padding: '10px 12px', marginTop: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <b style={{ color: 'var(--text)', fontSize: 13.5 }}>
              {row.name || 'Экран'}
            </b>
            {row.branch_name && (
              <span style={{ color: 'var(--muted)', fontSize: 12 }}>{row.branch_name}</span>
            )}
            <span style={{ color: 'var(--dim)', fontSize: 11.5, marginLeft: 'auto' }}>
              последний запрос: {seen(row.last_seen_at)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <div style={{
              flex: 1, color: 'var(--muted)', fontSize: 12, wordBreak: 'break-all',
              background: 'var(--card)', borderRadius: 6, padding: '6px 9px',
            }}>
              {linkOf(row)}
            </div>
            <button onClick={() => copy(linkOf(row))} style={{
              background: 'none', border: '1px solid var(--border)', borderRadius: 6,
              color: 'var(--muted)', padding: '6px 11px', fontSize: 12, cursor: 'pointer',
              fontFamily: 'inherit',
            }}>📋</button>
            <button onClick={() => drop(row)} style={{
              background: 'none', border: '1px solid var(--border)', borderRadius: 6,
              color: 'var(--danger)', padding: '6px 11px', fontSize: 12, cursor: 'pointer',
              fontFamily: 'inherit',
            }}>{t('Отозвать')}</button>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Где висит экран — «Офис, приёмная»"
          style={{
            flex: 1, background: 'var(--input)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text)', padding: '9px 12px', fontSize: 13,
            fontFamily: 'inherit', outline: 'none',
          }}
        />
        <button onClick={add} disabled={busy} style={{
          background: 'var(--accent)', border: 'none', borderRadius: 8, color: '#fff',
          padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          fontFamily: 'inherit', opacity: busy ? 0.6 : 1,
        }}>
          {t('Добавить экран')}
        </button>
      </div>
    </Card>
  );
}

// ─── Причины остановки пресса ────────────────────────────────────────────────
//
// Список был вшит в код, а цех у каждого свой: где-то плёнку заправляют раз в
// смену, где-то ДСП подвозят с улицы, а под уборку не останавливаются вовсе.
// Список, который нельзя поправить, заполняется «Другим» и перестаёт
// что-либо объяснять.
function StopReasonsPanel() {
  const { branch, save } = useBranchSettings();
  const [idle, setIdle]   = useState('');
  const [rows, setRows]   = useState([]);
  const [adding, setAdd]  = useState(false);
  const [name, setName]   = useState('');
  const [icon, setIcon]   = useState('');
  const [blocks, setBlocks] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    productionAPI.getStopReasons()
      .then(r => setRows(r.data.results || r.data))
      .catch(() => toast.error(t('Ошибка загрузки')));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (r) => {
    try {
      await productionAPI.updateStopReason(r.id, { is_active: !r.is_active });
      load();
      toast.success(r.is_active ? t('Отключена') : t('Включена'));
    } catch { toast.error(t('Ошибка')); }
  };

  const toggleBlocks = async (r) => {
    try {
      await productionAPI.updateStopReason(r.id, { blocks_work: !r.blocks_work });
      load();
    } catch { toast.error(t('Ошибка')); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Удалить причину «${r.name}»?`)) return;
    try {
      await productionAPI.deleteStopReason(r.id);
      load();
      toast.success(t('Удалена'));
    } catch (err) {
      toast.error(err.response?.data?.[0] || err.response?.data?.detail || t('Ошибка'));
    }
  };

  const add = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await productionAPI.createStopReason({
        name: name.trim(), icon: icon.trim(), is_active: true,
        blocks_work: blocks, order: (rows.length + 1) * 10,
      });
      setName(''); setIcon(''); setBlocks(true); setAdd(false); load();
      toast.success(t('Причина добавлена'));
    } catch { toast.error(t('Ошибка сохранения')); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (branch) setIdle(String(branch.press_idle_minutes ?? 0));
  }, [branch]);

  const saveIdle = async () => {
    const v = Math.max(0, Math.min(600, parseInt(idle, 10) || 0));
    setIdle(String(v));
    if (branch && v === (branch.press_idle_minutes ?? 0)) return;
    await save({ press_idle_minutes: v });
  };

  const inp = {
    padding: '9px 12px', background: 'var(--input)', border: '1px solid var(--border)',
    borderRadius: 8, color: 'var(--text)', fontSize: 14, fontFamily: 'inherit',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700 }}>
        ⏸ {t('Причины остановки пресса')}
      </h2>
      <p style={{ color: 'var(--dim)', fontSize: 13, marginTop: -8 }}>
        Из этого списка оператор выбирает, почему пресс встал. Отключённые
        причины у пресса не показываются, но записи с ними остаются.
        «Не закрывает экран» — для настройки режима: время идёт, а кнопки
        у оператора остаются живыми, иначе настраивать будет нечем.
      </p>

      {/* «Простой без причины» ставит не человек, а система — и порог, после
          которого она это делает, стоит здесь же, рядом с самой причиной. */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
              ⏳ Отмечать простой, если «+1» не нажимали
            </div>
            <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 3, maxWidth: 620 }}>
              Смена идёт, а листа давно нет — значит пресс стоит. Считается и при
              закрытом окне терминала: станок стоит одинаково, смотрит на него
              кто-нибудь или нет. 0 — не следить.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <input type="number" min={0} max={600} value={idle}
              onChange={e => setIdle(e.target.value)}
              onBlur={saveIdle}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()}
              style={{ ...inp, width: 84, textAlign: 'center', fontSize: 16 }} />
            <span style={{ color: 'var(--muted)', fontSize: 13 }}>мин</span>
          </div>
        </div>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Btn variant={adding ? 'ghost' : 'primary'} onClick={() => setAdd(!adding)}>
            {adding ? '✕ Отмена' : '+ Добавить причину'}
          </Btn>
        </div>

        {adding && (
          <div style={{
            background: 'var(--bg)', borderRadius: 10, padding: 14, marginBottom: 14,
            display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
          }}>
            <input value={icon} onChange={e => setIcon(e.target.value)}
              placeholder="🔧" style={{ ...inp, width: 70, textAlign: 'center', fontSize: 20 }} />
            <input autoFocus value={name} onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && add()}
              placeholder={t('Название, напр.: Отключили свет')}
              style={{ ...inp, flex: 1, minWidth: 220 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8,
                            color: 'var(--muted)', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={!blocks} onChange={e => setBlocks(!e.target.checked)} />
              {t('не закрывает экран')}
            </label>
            <Btn variant="success" size="md" disabled={!name.trim() || saving} onClick={add}>
              {saving ? '...' : '✅ Сохранить'}
            </Btn>
          </div>
        )}

        {rows.map(r => (
          <div key={r.id} style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '12px 0', borderBottom: '1px solid var(--border)',
            opacity: r.is_active ? 1 : 0.5,
          }}>
            <div style={{ fontSize: 24, width: 34, textAlign: 'center' }}>{r.icon || '⏸'}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
                {r.name}
                {r.is_system && (
                  <span style={{
                    marginLeft: 8, background: 'var(--accent-weak)', color: 'var(--accent)',
                    borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                  }}>{t('ставит система')}</span>
                )}
                {!r.blocks_work && (
                  <span style={{
                    marginLeft: 8, background: '#f59e0b18', color: '#f59e0b',
                    borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                  }}>{t('кнопки живые')}</span>
                )}
              </div>
            </div>

            {!r.is_system && (
              <button onClick={() => toggleBlocks(r)} title={t('Закрывает ли экран оператора')}
                style={{
                  background: 'none', border: '1px solid var(--border)', borderRadius: 7,
                  padding: '5px 12px', color: 'var(--muted)', fontSize: 12.5,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>
                {r.blocks_work ? t('закрывает экран') : t('не закрывает')}
              </button>
            )}

            <button onClick={() => toggle(r)} style={{
              background: r.is_active ? '#ef444415' : '#22c55e15',
              border: `1px solid ${r.is_active ? '#ef444430' : '#22c55e30'}`,
              borderRadius: 7, padding: '5px 14px',
              color: r.is_active ? '#ef4444' : '#22c55e',
              fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>{r.is_active ? t('Отключить') : t('Включить')}</button>

            {!r.is_system && (
              <button onClick={() => remove(r)} style={{
                background: 'none', border: 'none', color: 'var(--dim)',
                fontSize: 17, cursor: 'pointer',
              }}>🗑</button>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}

function PressNormPanel() {
  const [presses, setPresses] = useState([]);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(null);

  const load = () => productionAPI.getPresses().then(r => setPresses(r.data));
  useEffect(() => { load(); }, []);

  const handleSave = async (press) => {
    const norm = parseInt(editing[press.id] ?? press.norm_per_hour, 10);
    if (!norm || norm < 1) { toast.error(t("Норма должна быть > 0")); return; }
    setSaving(press.id);
    try {
      await productionAPI.setPressNorm(press.id, norm);
      toast.success(`Норма для ${press.name} обновлена`);
      setEditing(e => { const n = { ...e }; delete n[press.id]; return n; });
      load();
    } catch { toast.error(t("Ошибка")); }
    finally { setSaving(null); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700 }}>{t('⚙️ Нормы производительности прессов')}</h2>
      <p style={{ color: 'var(--dim)', fontSize: 13, marginTop: -8 }}>
        Норма — количество листов ДСП, которые пресс должен производить в час в нормальных условиях.
        Используется для расчёта эффективности (% выполнения нормы).
      </p>
      <Card>
        {presses.map(press => {
          const val = editing[press.id] !== undefined ? editing[press.id] : press.norm_per_hour;
          const isDirty = editing[press.id] !== undefined;
          return (
            <div key={press.id} style={{
              display: 'flex', alignItems: 'center', gap: 16,
              padding: '14px 0', borderBottom: '1px solid var(--border)',
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10, background: 'var(--accent-weak)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
              }}>🏭</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>{press.name}</div>
                <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 2 }}>
                  Статус: {press.status_display}
                  {press.current_texture_name && ` · Текстура: ${press.current_texture_name}`}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div>
                  <label style={{ display: 'block', color: 'var(--muted)', fontSize: 11, marginBottom: 4 }}>
                    Норма (листов/час)
                  </label>
                  <input
                    type="number" min={1} max={500} value={val}
                    onChange={e => setEditing(ed => ({ ...ed, [press.id]: e.target.value }))}
                    style={{
                      width: 100, padding: '8px 12px', background: 'var(--input)',
                      border: `1px solid ${isDirty ? 'var(--accent)' : 'var(--border)'}`,
                      borderRadius: 8, color: 'var(--text)', fontSize: 15,
                      fontWeight: 700, textAlign: 'center', outline: 'none',
                    }}
                  />
                </div>
                <div style={{ marginTop: 18 }}>
                  <Btn
                    size="sm"
                    variant={isDirty ? 'primary' : 'ghost'}
                    disabled={!isDirty || saving === press.id}
                    onClick={() => handleSave(press)}
                  >
                    {saving === press.id ? '...' : 'Сохранить'}
                  </Btn>
                </div>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

// ─── Быстрая статистика ────────────────────────────────────────────────────────
function QuickStats() {
  const [orders, setOrders] = useState([]);
  const [clearing, setClearing] = useState(false);
  
  useEffect(() => { salesAPI.getOrders().then(r => setOrders(r.data)); }, []);

  const statuses = ['pending', 'queue', 'production', 'ready', 'shipped', 'cancelled'];
  const STATUS_LABELS = {
    pending: 'Ожидает', queue: 'В очереди', production: 'В производстве',
    ready: 'Готово', shipped: 'Отгружено', cancelled: 'Отменено',
  };
  const STATUS_COLORS = {
    pending: '#f97316', queue: '#f59e0b', production: 'var(--accent)',
    ready: '#22c55e', shipped: '#8b5cf6', cancelled: '#ef4444',
  };

  // Остаток считается заново — от того, что прислала 1С.
  //
  // Раньше на этом месте стояла «Очистка системы»: она сносила и справочник
  // номенклатуры, и рецепты. Администратор приходит сюда за «начать заново», а
  // получал потерю месяца работы технолога. Очистка уехала к поставщику
  // программы, здесь — то, за чем сюда приходят на самом деле.
  const [resetInfo, setResetInfo] = useState(null);
  // Очередь в 1С выбрасывают отдельным решением. По умолчанию — нет: там
  // лежат новые контрагенты, которых 1С ещё не завела, и выбросить их значит
  // потерять связь — документы по этим клиентам в 1С потом не сядут.
  const [dropQueue, setDropQueue] = useState(false);
  const loadResetInfo = React.useCallback(() => {
    adminToolsAPI.stockResetInfo().then(r => setResetInfo(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadResetInfo(); }, [loadResetInfo]);

  const handleStockReset = async () => {
    const own = resetInfo?.own_rows ?? 0;
    const from1c = resetInfo?.from_1c_rows ?? 0;
    const snap = resetInfo?.last_snapshot;
    const pend = resetInfo?.pending_total ?? 0;
    if (!await dangerConfirm({
      title: 'Начать остаток заново',
      finalText: 'Да, обнулить остаток',
      steps: [
        `Будут удалены собственные записи склада MES: ${own}.\n`
        + `Останется остаток из 1С: ${from1c} записей.\n\n`
        + 'Смены, бирки, заказы, номенклатура и рецепты не трогаются.',

        `Остаток изменится сразу у всех: у менеджера, у склада, у директора.\n`
        + (snap ? `Последний остаток из 1С: ${snap}.` : 'Остаток из 1С ещё не приходил — после обнуления склад будет пустым до ближайшего обмена.')
        + (pend ? `\n\nВ очереди в 1С ещё ${pend} документов — они ${dropQueue ? 'БУДУТ ВЫБРОШЕНЫ' : 'останутся и уйдут в 1С как обычно'}.` : ''),

        'Делать это надо на границе смены: пресс не работает, склад ничего не '
        + 'принимает.\n\nСнимок базы система сделает сама — но вернуть остаток '
        + 'кнопкой отсюда нельзя.',
      ],
    })) return;
    setClearing(true);
    try {
      const { data } = await adminToolsAPI.stockReset({ drop_pending: dropQueue });
      toast.success(`Остаток начат заново · убрано записей: ${data.removed_rows}`
        + (data.dropped_queue ? ` · очередь в 1С очищена: ${data.dropped_queue}` : ''));
      loadResetInfo();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setClearing(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <h3 style={{ color: 'var(--text)', fontSize: 15, fontWeight: 700, marginBottom: 14 }}>{t('📊 Заказы по статусам')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {statuses.map(s => {
            const count = orders.filter(o => o.status === s).length;
            return (
              <div key={s} style={{ background: 'var(--bg)', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                <div style={{ color: STATUS_COLORS[s], fontSize: 26, fontWeight: 900 }}>{count}</div>
                <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>{STATUS_LABELS[s]}</div>
              </div>
            );
          })}
        </div>
      </Card>
      
      <Card style={{ border: '1px solid #f59e0b' }}>
        <h3 style={{ color: '#f59e0b', fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
          {t('Начать остаток заново')}
        </h3>
        <p style={{ color: 'var(--dim)', fontSize: 13, marginBottom: 12, lineHeight: 1.6 }}>
          Остаток считается по 1С. Пока MES вёл свой счёт параллельно, один и тот
          же лист попадал в остаток дважды — и своей записью о выпуске, и снимком
          из 1С. Кнопка убирает записи MES и оставляет присланное 1С.
          <br />
          <b>Работа завода не трогается:</b> смены, бирки, заказы, номенклатура и
          рецепты остаются как есть. Меняется только число на складе.
        </p>
        {resetInfo && (
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px',
                        fontSize: 13, color: 'var(--muted)', marginBottom: 14,
                        lineHeight: 1.9 }}>
            <div>Записи MES (будут убраны): <b style={{ color: 'var(--text)' }}>{resetInfo.own_rows}</b></div>
            <div>Записи из 1С (останутся): <b style={{ color: 'var(--text)' }}>{resetInfo.from_1c_rows}</b></div>
            <div>Последний остаток из 1С: <b style={{ color: 'var(--text)' }}>
              {resetInfo.last_snapshot || 'ещё не присылали'}</b></div>
            {resetInfo.pending_total > 0 && (
              <div style={{ color: '#f59e0b' }}>
                В очереди в 1С: {resetInfo.pending_total}
                {' ('}{(resetInfo.pending || []).map(p => `${p.entity} ${p.n}`).join(', ')}{')'}
              </div>
            )}
          </div>
        )}
        {resetInfo && resetInfo.pending_total > 0 && (
          <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start',
                          fontSize: 13, color: 'var(--muted)', marginBottom: 14,
                          cursor: 'pointer', lineHeight: 1.55, maxWidth: 640 }}>
            <input type="checkbox" checked={dropQueue}
                   onChange={e => setDropQueue(e.target.checked)}
                   style={{ marginTop: 3, width: 16, height: 16, cursor: 'pointer' }} />
            <span>
              Выбросить и очередь в 1С ({resetInfo.pending_total})
              <br />
              <span style={{ color: 'var(--dim)', fontSize: 12 }}>
                Без этого документы уйдут в 1С как обычно. С этим — исчезнут
                насовсем; новые контрагенты из очереди в 1С уже не заведутся, и
                документы по ним туда не сядут.
              </span>
            </span>
          </label>
        )}
        <Btn variant="danger" onClick={handleStockReset} disabled={clearing}>
          {clearing ? 'Обнуление…' : 'Начать остаток заново'}
        </Btn>
      </Card>
    </div>
  );
}

// ─── ГЛАВНАЯ ──────────────────────────────────────────────────────────────────
// ─── Пометка на удаление ──────────────────────────────────────────────────────
// Два шага, как в 1С: сначала документ помечают, потом администратор удаляет
// помеченное. Пока документ помечен — работать с ним нельзя (проверяет бэкенд).
function DeletionMarkPanel() {
  const [entities, setEntities] = useState([]);
  const [entity, setEntity]     = useState('');
  const [scope, setScope]       = useState('all');   // all | marked
  const [rows, setRows]         = useState([]);
  const [sel, setSel]           = useState(new Set());
  const [reason, setReason]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState(false);

  const loadEntities = () =>
    adminToolsAPI.getEntities()
      .then(r => {
        setEntities(r.data.entities || []);
        setEntity(prev => prev || (r.data.entities[0] || {}).key || '');
      })
      .catch(() => toast.error(t('Не удалось загрузить список документов')));

  useEffect(() => { loadEntities(); }, []);

  const loadRows = React.useCallback(() => {
    if (!entity) return;
    setLoading(true);
    setSel(new Set());
    adminToolsAPI.getDocuments({ entity, scope, limit: 100 })
      .then(r => setRows(r.data.rows || []))
      .catch(err => toast.error(err.response?.data?.error || 'Ошибка загрузки'))
      .finally(() => setLoading(false));
  }, [entity, scope]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const toggle = (id) => setSel(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const picked      = rows.filter(r => sel.has(r.id));
  const canMark     = picked.filter(r => r.can_mark);
  const canUnmark   = picked.filter(r => r.can_unmark);
  const canDelete   = picked.filter(r => r.marked);

  const after = (msg) => { toast.success(msg); loadRows(); loadEntities(); };

  const doMark = async () => {
    if (canMark.length === 0) { toast.error(t('Нечего помечать')); return; }
    setBusy(true);
    try {
      const { data } = await adminToolsAPI.mark(entity, canMark.map(r => r.id), reason);
      if (data.refused?.length) {
        toast.error(`Не помечено ${data.refused.length}: ${data.refused[0].why}`);
      }
      after(`Помечено: ${data.marked.length}`);
      setReason('');
    } catch (err) { toast.error(err.response?.data?.error || 'Ошибка'); }
    finally { setBusy(false); }
  };

  const doUnmark = async () => {
    if (canUnmark.length === 0) { toast.error(t('Нечего снимать')); return; }
    setBusy(true);
    try {
      const { data } = await adminToolsAPI.unmark(entity, canUnmark.map(r => r.id));
      after(`Пометка снята: ${data.unmarked.length}`);
    } catch (err) { toast.error(err.response?.data?.error || 'Ошибка'); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (canDelete.length === 0) { toast.error(t('Удалять можно только помеченное')); return; }
    const ok = await confirmToast(
      `Удалить безвозвратно ${canDelete.length} шт.? Вместе с документом уйдут связанные записи.`,
      { danger: true, confirmText: 'Удалить' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await adminToolsAPI.deleteMarked(entity, canDelete.map(r => r.id));
      if (data.failed?.length) {
        toast.error(`Не удалено ${data.failed.length}: ${data.failed[0].why}`);
      }
      after(`Удалено: ${data.deleted.length}`);
    } catch (err) { toast.error(err.response?.data?.error || 'Ошибка'); }
    finally { setBusy(false); }
  };

  const TD = { padding: '9px 10px', borderBottom: '1px solid var(--border)', fontSize: 13, verticalAlign: 'top' };

  return (
    <Card>
      <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
        🗑 Пометка на удаление
      </h2>
      <p style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 14 }}>
        Сначала пометьте документ, потом удалите помеченное. Пока документ помечен,
        работать с ним нельзя. Если на документе висят другие дела — они показаны красным.
      </p>

      {/* Типы документов */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {entities.map(e => (
          <button key={e.key} onClick={() => setEntity(e.key)} style={{
            background: entity === e.key ? 'var(--accent-weak)' : 'var(--bg)',
            border: '1px solid ' + (entity === e.key ? 'var(--accent)' : 'var(--border)'),
            borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12,
            color: entity === e.key ? 'var(--accent)' : 'var(--muted)', fontWeight: 600,
          }}>
            {e.label}
            {e.marked_count > 0 && (
              <span style={{ marginLeft: 6, background: '#ef4444', color: '#fff', borderRadius: 9, padding: '1px 6px', fontSize: 10 }}>
                {e.marked_count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <Sel value={scope} onChange={e => setScope(e.target.value)} style={{ width: 200 }}>
          <option value="all">{t('Все документы')}</option>
          <option value="marked">{t('Только помеченные')}</option>
        </Sel>
        <Inp placeholder={t('Причина (необязательно)')} value={reason}
          onChange={e => setReason(e.target.value)} style={{ width: 260 }} />
        <Btn size="sm" onClick={doMark} disabled={busy || canMark.length === 0}>
          ⚑ Пометить ({canMark.length})
        </Btn>
        <Btn size="sm" variant="ghost" onClick={doUnmark} disabled={busy || canUnmark.length === 0}>
          ↩ Снять ({canUnmark.length})
        </Btn>
        <Btn size="sm" variant="danger" onClick={doDelete} disabled={busy || canDelete.length === 0}
          style={{ marginLeft: 'auto' }}>
          🗑 Удалить помеченные ({canDelete.length})
        </Btn>
      </div>

      {loading ? (
        <p style={{ color: 'var(--muted)', fontSize: 13, padding: 16 }}>{t('Загрузка…')}</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--muted)', fontSize: 13, padding: 16 }}>{t('Документов нет')}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ ...TD, width: 34 }}></th>
                <th style={{ ...TD, textAlign: 'left' }}>Документ</th>
                <th style={{ ...TD, textAlign: 'left', width: 150 }}>Автор</th>
                <th style={{ ...TD, textAlign: 'left', width: 110 }}>Создан</th>
                <th style={{ ...TD, textAlign: 'left' }}>Что мешает / пометка</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{
                  // Помеченный — серый и зачёркнутый, но из списка не пропадает
                  opacity: r.marked ? 0.55 : 1,
                  background: r.marked ? 'var(--bg)' : 'transparent',
                }}>
                  <td style={TD}>
                    <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} />
                  </td>
                  <td style={TD}>
                    <div style={{
                      color: 'var(--text)', fontWeight: 600,
                      textDecoration: r.marked ? 'line-through' : 'none',
                    }}>{r.title}</div>
                    <div style={{ color: 'var(--dim)', fontSize: 11 }}>{r.subtitle}</div>
                  </td>
                  <td style={{ ...TD, color: 'var(--muted)' }}>{r.author || '—'}</td>
                  <td style={{ ...TD, color: 'var(--muted)', fontSize: 12 }}>
                    {new Date(r.created_at).toLocaleDateString('ru')}
                  </td>
                  <td style={TD}>
                    {r.marked && (
                      <div style={{ color: '#f59e0b', fontSize: 12, fontWeight: 600 }}>
                        ⚑ помечен{r.marked_by ? ` — ${r.marked_by}` : ''}
                        {r.reason ? ` · ${r.reason}` : ''}
                      </div>
                    )}
                    {r.blockers.map((b, i) => (
                      <div key={i} style={{ color: '#ef4444', fontSize: 12 }}>• {b}</div>
                    ))}
                    {!r.marked && r.blockers.length === 0 && (
                      <span style={{ color: '#22c55e', fontSize: 12 }}>✓ ничего не висит</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// Разделы рисуются в общем боковом меню (Layout), под пунктом
// «Администрирование»: своя колонка кнопок внутри страницы занимала место
// второй раз. Блок «Страницы ролей» убран — все эти страницы у админа и так
// перечислены в боковом меню, он повторял его один в один.
//
// ── Дверь «Настройка системы» убрана ───────────────────────────────────────
//
// Она стояла между меню и делом, и за ней лежали ещё десять плиток. Путь до
// «Пользователей» был такой: «Администрирование» → «Настройка системы» →
// плитка «Настройки системы»… которая открывала СОВСЕМ ДРУГОЙ экран — общие
// правила. Два названия, отличающихся одной буквой, одно внутри другого.
// В этом месте терялись все, включая тех, кто это писал.
//
// Теперь все десять стоят прямо в меню, отдельной группой внизу: их видно
// целиком, ни одно не спрятано за другим, и до любого — одно нажатие.
// На «Главном» они продолжают лежать плитками с подсказками — как карта, а не
// как обязательный этаж на пути.
const SECTIONS = [
  { title: '', items: [
    { key: 'home',    label: '🏠 Главное' },
  ]},
  { title: 'Каждый день', items: [
    { key: 'rejects', label: '📥 Разбор с прессов' },
    { key: 'stats',   label: '📊 Статистика' },
  ]},
  { title: 'Настройка', items: [
    // «Настройки системы» — на букву отличалось от двери, за которой лежало.
    { key: 'general', label: '🔧 Общие правила' },
    { key: 'users',   label: '👥 Пользователи' },
    { key: 'recipes', label: '🧾 Рецепты' },
    // «Нормы прессов» здесь и «Характеристики прессов» у технолога — оба про
    // листы в час. Пишем прямо, что за число.
    { key: 'norms',   label: '⚙️ Норма выработки прессов' },
    { key: 'stops',   label: '⏸ Причины остановки' },
    { key: 'quality', label: '🏷 Сорта и причины брака' },
    { key: 'wh',      label: '🏬 Склады' },
    // «Моноблоки прессов» — слово из документации, а не из цеха.
    { key: 'devices', label: '🔌 Терминалы у прессов' },
    { key: 'ai',      label: '🚦 Светофор ИИ' },
    { key: 'trash',   label: '🗑 Пометка на удаление' },
    { key: 'testdata', label: '🧪 Тестовые данные' },
  ]},
];

// Те же разделы плитками на «Главном» — с подсказкой, что внутри. В меню
// подсказку не покажешь, а половина этих названий сама за себя не говорит.
const SETUP_TILES = [
  { key: 'general', icon: '🔧', title: 'Общие правила', small: true, color: 'var(--accent)',
    hint: 'Заказы без остатка, порог простоя пресса' },
  { key: 'users',   icon: '👥', title: 'Пользователи', small: true, color: '#0ea5e9',
    hint: 'Кто заходит в программу, под какой ролью и с каким паролем' },
  { key: 'recipes', icon: '🧾', title: 'Рецепты', small: true, color: '#22c55e',
    hint: 'Из чего делается лист — без них сырьё не списывается' },
  { key: 'norms',   icon: '⚙️', title: 'Норма выработки прессов', small: true, color: '#8b5cf6',
    hint: 'Сколько листов в час считается нормой' },
  { key: 'stops',   icon: '⏸', title: 'Причины остановки', small: true, color: '#f59e0b',
    hint: 'Из чего оператор выбирает, когда пресс встал' },
  { key: 'quality', icon: '🏷', title: 'Сорта и причины брака', small: true, color: '#ef4444',
    hint: 'Из чего приёмщик выбирает, когда лист не 1-го сорта' },
  { key: 'wh',      icon: '🏬', title: 'Склады', small: true, color: '#64748b',
    hint: 'Какой склад за какую категорию отвечает' },
  { key: 'devices', icon: '🔌', title: 'Терминалы у прессов', small: true, color: '#64748b',
    hint: 'Моноблоки у станков и их ключи' },
  // Своей страницей живёт с самого начала, но ссылки на неё не было нигде: её
  // открывали, помня адрес наизусть. Помнил его один человек.
  { key: 'integration', icon: '⇆', title: 'Обмен с 1С', small: true, color: '#14b8a6',
    hint: 'Адрес 1С, ключ обмена и очередь документов' },
  { key: 'ai',      icon: '🚦', title: 'Светофор ИИ', small: true, color: '#22c55e',
    hint: 'С каких цифр брака и простоя ИИ красит цех в жёлтый и красный' },
  { key: 'trash',   icon: '🗑', title: 'Пометка на удаление', small: true, color: '#ef4444',
    hint: 'Что помечено к удалению и ждёт решения' },
  { key: 'testdata', icon: '🧪', title: 'Тестовые данные', small: true, color: '#64748b',
    hint: 'Заполнить тренажёр правдоподобными данными' },
];

// ─── Склады: какой за что отвечает ───────────────────────────────────────────
//
// Производство брало «первый активный склад», и весь выпуск оседал на
// «Основном», хотя ЛДСП лежит на складе готовой продукции, плёнка на
// плёночном, ДСП на дсп-шном. В 1С уходил документ не с тем складом —
// движение получалось не там, где товар, и остатки расходились молча.
//
// Назначение хранится на самом складе. Отмеченный склад становится тем, куда
// цех приходует выпуск и откуда списывает сырьё этой категории; тот же склад
// уходит в документ для 1С, и она уже не гадает.
const WH_KINDS = [
  { key: '',      label: '— не указан —' },
  { key: 'paper', label: '📄 Бумага / плёнка' },
  { key: 'dsp',   label: '🪵 ДСП' },
  { key: 'ldsp',  label: '📦 Готовая продукция (ЛДСП)' },
];

const WH_ROLES = [
  { key: 'ldsp',  label: 'Сюда приходуется выпуск цеха', short: 'выпуск ЛДСП' },
  { key: 'dsp',   label: 'Отсюда списывается ДСП',        short: 'расход ДСП' },
  { key: 'paper', label: 'Отсюда списывается плёнка',     short: 'расход плёнки' },
];

function WarehouseSettings() {
  const [rows, setRows]     = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoad]  = useState(true);
  const [busy, setBusy]     = useState(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName]     = useState('');
  const [newBranch, setNewBranch] = useState('');

  const load = useCallback(() => {
    setLoad(true);
    warehouseAPI.getWarehouses()
      .then(r => setRows(r.data.results || r.data || []))
      .catch(() => toast.error(t('Склады не загрузились')))
      .finally(() => setLoad(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    branchAPI.getList()
      .then(r => setBranches(r.data.results || r.data || []))
      .catch(() => {});
  }, []);

  // Склад нового цеха заводить было негде: до второго цеха его создавала сама
  // 1С первым же остатком и клала в единственный филиал. Теперь MES не
  // угадывает — склад в чужом цехе отдал бы ему чужой остаток, — и создать
  // склад можно только здесь.
  const create = async () => {
    if (!newName.trim()) { toast.error(t('Название склада')); return; }
    if (!newBranch)      { toast.error(t('Выберите филиал')); return; }
    setBusy('new');
    try {
      await warehouseAPI.createWarehouse({ name: newName.trim(), branch: newBranch });
      toast.success(t('Склад заведён'));
      setNewName(''); setAdding(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setBusy(null); }
  };

  const save = async (w, patch) => {
    setBusy(w.id);
    try {
      await warehouseAPI.updateWarehouse(w.id, patch);
      toast.success(t('Сохранено'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Ошибка');
    } finally { setBusy(null); }
  };

  if (loading) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>{t('Загрузка...')}</p>;

  const SEL = {
    background: 'var(--input)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text)', padding: '8px 10px', fontSize: 13, fontFamily: 'inherit',
    width: '100%', minWidth: 0,
  };

  // Кто назначен на роль В ЭТОМ ЦЕХЕ. Роль ищется внутри цеха: склад с ролью
  // «сюда приходуется выпуск» есть у каждого цеха, и раньше, когда цех был
  // один, holder был один на завод. Со вторым цехом такой ответ был бы
  // неверным — и на экране, и в производстве.
  const holder = (role, branchId) =>
    rows.find(w => w.default_for_category === role && String(w.branch) === String(branchId));

  // Цеха показываем в том порядке, в каком идут склады: свой цех у админа
  // обычно первый, и его роли должны быть сверху.
  const shopIds = [];
  rows.forEach(w => {
    const id = String(w.branch || '');
    if (!shopIds.includes(id)) shopIds.push(id);
  });
  const shopName = (id) => (branches.find(b => String(b.id) === String(id)) || {}).name
    || (id ? 'цех ' + id : 'без цеха');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.55, maxWidth: 820 }}>
        Отмеченный склад — тот, куда цех приходует выпуск и откуда списывает сырьё
        этой категории. Он же уходит в документ для 1С, и она не выбирает склад
        сама. Пока роль ни за кем не закреплена, движение падает на первый
        попавшийся склад — и остаток расходится с реальным.
      </div>

      {shopIds.map(sid => (
      <div key={sid || 'none'}>
        <div style={{ color: 'var(--dim)', fontSize: 11, letterSpacing: '.08em',
                      textTransform: 'uppercase', marginBottom: 6 }}>
          {shopName(sid)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
        {WH_ROLES.map(r => {
          const w = holder(r.key, sid);
          return (
            <div key={r.key} style={{
              background: 'var(--bg)', border: '1px solid ' + (w ? 'var(--border)' : 'var(--danger)'),
              borderRadius: 10, padding: '12px 14px',
            }}>
              <div style={{ color: 'var(--dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                {r.short}
              </div>
              <div style={{ color: w ? 'var(--text)' : 'var(--danger)', fontSize: 14, fontWeight: 700, marginTop: 4 }}>
                {w ? w.name : 'не назначен'}
              </div>
            </div>
          );
        })}
        </div>
      </div>
      ))}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map(w => (
          <div key={w.id} style={{
            background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
            padding: '12px 14px', display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, .8fr) minmax(0, 1fr) minmax(0, 1.2fr)',
            gap: 10, alignItems: 'center',
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {w.name}
              </div>
              {/* Код MES — то, что бухгалтер вписывает в 1С («Код склада MES»).
                  Без него 1С опознаёт склад по названию, и при первом же
                  расхождении заводится второй: остаток уходит не туда. */}
              <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 3 }}>
                код для 1С: <b style={{ color: 'var(--text)' }}>{w.mes_code}</b>
                <span style={{ marginLeft: 8 }}>
                  GUID: {w.code ? w.code.slice(0, 8) + '…' : '— нет'}
                </span>
              </div>
            </div>

            <div>
              <div style={{ color: 'var(--dim)', fontSize: 10.5, marginBottom: 3 }}>ЦЕХ</div>
              <select value={w.branch || ''} disabled={busy === w.id} style={SEL}
                onChange={e => save(w, { branch: e.target.value })}>
                <option value="">— не указан —</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>

            <div>
              <div style={{ color: 'var(--dim)', fontSize: 10.5, marginBottom: 3 }}>ЧТО ЛЕЖИТ</div>
              <select value={w.kind || ''} disabled={busy === w.id} style={SEL}
                onChange={e => save(w, { kind: e.target.value })}>
                {WH_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </div>

            <div>
              <div style={{ color: 'var(--dim)', fontSize: 10.5, marginBottom: 3 }}>РОЛЬ В ПРОИЗВОДСТВЕ</div>
              <select value={w.default_for_category || ''} disabled={busy === w.id} style={SEL}
                onChange={e => save(w, { default_for_category: e.target.value })}>
                <option value="">— не участвует —</option>
                {WH_ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>

      {adding ? (
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)',
                      borderRadius: 10, padding: '12px 14px', display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr) auto auto',
                      gap: 10, alignItems: 'center' }}>
          <input value={newName} onChange={e => setNewName(e.target.value)}
            placeholder={t('Название склада — как в 1С')} style={SEL} />
          <select value={newBranch} onChange={e => setNewBranch(e.target.value)} style={SEL}>
            <option value="">{t('— цех —')}</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <button type="button" onClick={create} disabled={busy === 'new'}
            style={{ background: 'var(--accent)', border: 'none', borderRadius: 8,
                     color: '#fff', padding: '9px 16px', fontSize: 13, fontWeight: 700,
                     cursor: 'pointer', fontFamily: 'inherit' }}>
            {t('Завести')}
          </button>
          <button type="button" onClick={() => { setAdding(false); setNewName(''); }}
            style={{ background: 'none', border: 'none', color: 'var(--dim)',
                     fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
            {t('Отмена')}
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)}
          style={{ alignSelf: 'flex-start', background: 'var(--bg)',
                   border: '1px solid var(--border)', borderRadius: 8,
                   color: 'var(--accent)', padding: '9px 14px', fontSize: 13,
                   fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          + {t('Завести склад')}
        </button>
      )}

      <div style={{ color: 'var(--dim)', fontSize: 12, lineHeight: 1.5 }}>
        Роль назначается в каждом цехе своя: цех берёт склад со своей ролью и
        чужой не трогает. Внутри одного цеха роль держит один склад — назначив
        её второму, производство возьмёт склад с меньшим номером. Склад без
        роли остаётся обычным — на него можно приходовать вручную.
        <br />
        Склад нового цеха заводят здесь, а его «код для 1С» вписывают в 1С:
        «Настройка MES» → «Склады» → «Код склада MES». Тогда остаток приедет
        именно на него, а не на новый склад, созданный обменом.
      </div>
    </div>
  );
}

// ─── Сорта и причины брака ───────────────────────────────────────────────────
//
// Приёмщик, принимая стопку из цеха, ставит листу сорт и говорит, ПОЧЕМУ он не
// высший. Сорта правились у технолога, а причины — нигде: список отдавался
// только на чтение, и поправить его можно было лишь в админке Django. Список,
// который нельзя поправить, быстро заполняется «Прочим»: причины у каждого
// завода свои, и словами тоже свои.
//
// Причину не удаляем, а выключаем: за ней стоят разобранные листы, и удалить
// её значит стереть, почему они стали браком.
const DEFECT_AREAS = [
  { key: 'mode',     label: 'Пресс и режим' },
  { key: 'material', label: 'Материал' },
  { key: 'handling', label: 'Обращение с листом' },
  { key: 'other',    label: 'Прочее' },
];

function QualityRefsPanel() {
  const [reasons, setReasons] = useState([]);
  const [grades, setGrades]   = useState([]);
  const [busy, setBusy]       = useState(null);
  const [name, setName]       = useState('');
  const [icon, setIcon]       = useState('');
  const [area, setArea]       = useState('mode');

  const load = useCallback(() => {
    sheetAPI.defectReasons(true)
      .then(r => setReasons(r.data || []))
      .catch(() => toast.error(t('Причины не загрузились')));
    gradeAPI.getGradeTypes()
      .then(r => setGrades(r.data || []))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) { toast.error(t('Назовите причину')); return; }
    setBusy('new');
    try {
      await sheetAPI.createDefectReason({ name: name.trim(), icon: icon.trim(), area });
      setName(''); setIcon('');
      toast.success(t('Причина добавлена'));
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Ошибка'); }
    finally { setBusy(null); }
  };

  const save = async (r, patch) => {
    setBusy(r.id);
    try {
      await sheetAPI.updateDefectReason(r.id, patch);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Ошибка'); }
    finally { setBusy(null); }
  };

  const toggleGrade = async (g) => {
    if (g.is_default) return;
    setBusy('g' + g.id);
    try {
      await gradeAPI.updateGradeType(g.id, { is_active: !g.is_active });
      load();
    } catch { toast.error(t('Ошибка')); }
    finally { setBusy(null); }
  };

  const SEL = {
    background: 'var(--input)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text)', padding: '8px 10px', fontSize: 13, fontFamily: 'inherit',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.55, maxWidth: 820 }}>
        Из этого списка приёмщик выбирает, почему лист не 1-го сорта. Причина
        не обязательна — держать приёмку из-за невыбранной кнопки нельзя, — но
        именно она отвечает на вопрос, что чинить: пузырь это температура и
        выдержка, непроклей — давление и плёнка, царапина — как везли.
        <br />
        Выключенная причина остаётся в разборе прошлых листов, но приёмщику
        больше не предлагается.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {reasons.map(r => (
          <div key={r.id} style={{
            background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
            padding: '10px 14px', display: 'grid',
            gridTemplateColumns: '44px minmax(0, 1.4fr) minmax(0, 1fr) auto',
            gap: 10, alignItems: 'center', opacity: r.is_active ? 1 : .55,
          }}>
            <input value={r.icon || ''} maxLength={2} disabled={busy === r.id}
              onChange={e => save(r, { icon: e.target.value })}
              style={{ ...SEL, textAlign: 'center', width: 44 }} />
            <input defaultValue={r.name} disabled={busy === r.id}
              onBlur={e => (e.target.value.trim() && e.target.value !== r.name
                ? save(r, { name: e.target.value.trim() }) : null)}
              style={SEL} />
            <select value={r.area} disabled={busy === r.id} style={SEL}
              onChange={e => save(r, { area: e.target.value })}>
              {DEFECT_AREAS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
            <button type="button" disabled={busy === r.id}
              onClick={() => save(r, { is_active: !r.is_active })}
              style={{
                background: 'none', border: '1px solid var(--border)', borderRadius: 8,
                color: r.is_active ? 'var(--muted)' : 'var(--accent)',
                padding: '7px 12px', fontSize: 12.5, cursor: 'pointer',
                fontFamily: 'inherit', whiteSpace: 'nowrap',
              }}>
              {r.is_active ? 'Выключить' : 'Включить'}
            </button>
          </div>
        ))}
      </div>

      <div style={{
        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
        padding: '12px 14px', display: 'grid',
        gridTemplateColumns: '44px minmax(0, 1.4fr) minmax(0, 1fr) auto',
        gap: 10, alignItems: 'center',
      }}>
        <input value={icon} maxLength={2} placeholder="🫧"
          onChange={e => setIcon(e.target.value)}
          style={{ ...SEL, textAlign: 'center', width: 44 }} />
        <input value={name} placeholder={t('Новая причина — как её называют в цехе')}
          onChange={e => setName(e.target.value)} style={SEL} />
        <select value={area} onChange={e => setArea(e.target.value)} style={SEL}>
          {DEFECT_AREAS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
        </select>
        <button type="button" onClick={add} disabled={busy === 'new'}
          style={{
            background: 'var(--accent)', border: 'none', borderRadius: 8, color: '#fff',
            padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'inherit',
          }}>
          {t('Добавить')}
        </button>
      </div>

      <div>
        <div style={{ color: 'var(--dim)', fontSize: 11, letterSpacing: '.08em',
                      textTransform: 'uppercase', marginBottom: 8 }}>
          Сорта
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.5,
                      marginBottom: 10, maxWidth: 820 }}>
          Чем приёмщик помечает лист. «Брак плёнки» и «Брак ДСП» относятся к
          материалу — их бракуют до пресса, листа тогда ещё нет, и складу при
          пересортице они не предлагаются. Заводят сорта у технолога, здесь их
          можно включить и выключить.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {grades.map(g => (
            <button key={g.id} type="button" disabled={g.is_default || busy === 'g' + g.id}
              onClick={() => toggleGrade(g)}
              title={g.is_default ? 'Сорт по умолчанию — выключить нельзя' : 'Включить или выключить'}
              style={{
                background: g.is_active ? 'var(--accent-weak)' : 'var(--bg)',
                border: '1px solid var(--border)', borderRadius: 999,
                color: g.is_active ? 'var(--accent)' : 'var(--dim)',
                padding: '7px 14px', fontSize: 12.5, fontWeight: 600,
                cursor: g.is_default ? 'default' : 'pointer', fontFamily: 'inherit',
              }}>
              {g.name}{g.scope === 'material' ? ' · материал' : ''}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── Моноблоки у прессов ─────────────────────────────────────────────────────
//
// У пресса стоит моноблок, к нему подключён станок. Локальная программа снимает
// показания и шлёт их в MES по ключу. Ключ вписывают в эту программу один раз —
// здесь его берут и сюда же возвращаются, когда моноблок меняют.
//
// Ключ показан целиком: прятать его на экране, куда заходит только админ, —
// мешать работе без выигрыша. Сменить ключ можно в одно нажатие: старый сразу
// перестаёт работать.
function PressDevices() {
  const [rows, setRows]   = useState([]);
  const [load, setLoad]   = useState(true);
  const [busy, setBusy]   = useState(null);
  const [shown, setShown] = useState({});

  const reload = useCallback(() => {
    setLoad(true);
    productionAPI.getPressDevices()
      .then(r => setRows(r.data.results || []))
      .catch(() => toast.error(t('Не удалось загрузить список')))
      .finally(() => setLoad(false));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const act = async (pressId, action, extra) => {
    setBusy(pressId);
    try {
      await productionAPI.savePressDevice({ press_id: pressId, action, ...(extra || {}) });
      toast.success(t('Сохранено'));
      reload();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Ошибка');
    } finally { setBusy(null); }
  };

  const copy = (key) => {
    navigator.clipboard?.writeText(key)
      .then(() => toast.success(t('Ключ скопирован')))
      .catch(() => toast.error(t('Скопируйте вручную')));
  };

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit',
        hour: '2-digit', minute: '2-digit' })
    : '—';

  if (load) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>{t('Загрузка...')}</p>;

  const BTN = {
    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--muted)', padding: '7px 12px', fontSize: 12.5, cursor: 'pointer',
    fontFamily: 'inherit', whiteSpace: 'nowrap',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.55, maxWidth: 820 }}>
        Программа на моноблоке отправляет показания станка: температуру верхней и
        нижней плиты, давление, время выдержки, а также температуру и влажность в
        цехе. Оператор больше не набирает эти цифры по памяти — он подставляет их
        одной кнопкой при старте смены.
        <br />
        Ключ выдаётся на каждый пресс отдельно: показание привязано к прессу самим
        ключом, и при замене моноблока меняется один ключ, а не весь цех.
      </div>

      <div style={{
        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
        padding: '12px 14px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.7,
      }}>
        <div style={{ color: 'var(--dim)', fontSize: 10.5, letterSpacing: '.1em',
                      textTransform: 'uppercase', marginBottom: 6 }}>
          Куда слать
        </div>
        <code style={{ color: 'var(--text)' }}>
          POST {window.location.origin}/api/v1/production/telemetry/press/
        </code>
        <br />
        <span>заголовок </span><code style={{ color: 'var(--text)' }}>X-API-Key: ключ пресса</code>
        <br />
        <span>тело </span>
        <code style={{ color: 'var(--text)' }}>
          {'{"temp_top":190,"temp_bottom":195,"pressure":12,"hold_seconds":18,'
           + '"shop_temp":24,"shop_humidity":47}'}
        </code>
        <div style={{ marginTop: 6, color: 'var(--dim)' }}>
          Присылать можно не всё сразу: чего станок не прочитал, то пропускается,
          остальное принимается.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map(r => {
          const on = r.has_device && r.is_active;
          const last = r.last_reading;
          return (
            <div key={r.press_id} style={{
              background: 'var(--bg)', borderRadius: 10, padding: '13px 15px',
              border: '1px solid ' + (on ? 'var(--border)' : 'var(--danger)'),
              display: 'flex', flexDirection: 'column', gap: 9,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <b style={{ color: 'var(--text)', fontSize: 15 }}>{r.press}</b>
                <span style={{
                  fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '2px 8px',
                  background: on ? '#22c55e20' : '#ef444420',
                  color: on ? '#22c55e' : '#ef4444',
                }}>
                  {!r.has_device ? 'НЕТ КЛЮЧА' : (r.is_active ? 'РАБОТАЕТ' : 'ОТКЛЮЧЁН')}
                </span>
                {/* Что ключу позволено. Смена — это списание сырья и закрытие
                    позиций, поэтому право видно рядом со статусом, а не
                    прячется в настройках. */}
                {r.has_device && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '2px 8px',
                    background: r.mode === 'terminal' ? '#0ea5e920' : 'var(--card)',
                    color: r.mode === 'terminal' ? '#0ea5e9' : 'var(--dim)',
                  }}>
                    {r.mode === 'terminal' ? 'ВЕДЁТ СМЕНУ' : 'ТОЛЬКО ПОКАЗАНИЯ'}
                  </span>
                )}
                <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 'auto' }}>
                  связь: {fmt(r.last_seen_at)}{r.last_ip ? ' · ' + r.last_ip : ''}
                  {' · показаний: '}{r.readings}
                  {r.agent_version ? ' · ' + r.agent_version : ''}
                </span>
              </div>

              {last && (
                <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>
                  последнее: плиты{' '}
                  <b style={{ color: 'var(--text)' }}>
                    {[last.temp_top, last.temp_bottom].filter(x => x != null).join(' / ') || '—'}
                  </b>{' °C · давление '}
                  <b style={{ color: 'var(--text)' }}>{last.pressure ?? '—'}</b>
                  {' · выдержка '}
                  <b style={{ color: 'var(--text)' }}>{last.hold_seconds ?? '—'}</b>
                  {' сек · цех '}
                  <b style={{ color: 'var(--text)' }}>{last.shop_temp ?? '—'}</b>
                  {'° / '}
                  <b style={{ color: 'var(--text)' }}>{last.shop_humidity ?? '—'}</b>{'%'}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {r.has_device ? (
                  <>
                    <code
                      onClick={() => copy(r.api_key)}
                      title={t('Нажмите, чтобы скопировать')}
                      style={{
                        flex: '1 1 260px', minWidth: 0, cursor: 'pointer',
                        background: 'var(--card)', border: '1px solid var(--border)',
                        borderRadius: 8, padding: '8px 11px', fontSize: 12.5,
                        color: 'var(--text)', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                      {shown[r.press_id] ? r.api_key : '•'.repeat(28)}
                    </code>
                    <button style={BTN} disabled={busy === r.press_id}
                      onClick={() => setShown(s => ({ ...s, [r.press_id]: !s[r.press_id] }))}>
                      {shown[r.press_id] ? t('Скрыть') : t('Показать')}
                    </button>
                    <button style={BTN} disabled={busy === r.press_id}
                      onClick={() => copy(r.api_key)}>{t('Копировать')}</button>
                    <button style={BTN} disabled={busy === r.press_id}
                      onClick={() => act(r.press_id, r.is_active ? 'disable' : 'enable')}>
                      {r.is_active ? t('Отключить') : t('Включить')}
                    </button>
                    {/* Расширение прав спрашиваем: программа, ведущая смену,
                        списывает сырьё и закрывает позиции заказа. Обратно —
                        без вопроса, сужать права всегда безопасно. */}
                    <button style={BTN} disabled={busy === r.press_id}
                      onClick={async () => {
                        const toTerminal = r.mode !== 'terminal';
                        if (toTerminal && !await confirmToast(
                          'Разрешить этому ключу вести смену целиком? Программа '
                          + 'сможет начинать задания, считать листы, отмечать брак '
                          + 'и закрывать позиции — со списанием сырья.',
                          { confirmText: 'Разрешить' })) return;
                        act(r.press_id, 'mode',
                            { mode: toTerminal ? 'terminal' : 'telemetry' });
                      }}>
                      {r.mode === 'terminal'
                        ? t('Только показания') : t('Разрешить смену')}
                    </button>
                    <button
                      style={{ ...BTN, borderColor: 'var(--danger)', color: 'var(--danger)' }}
                      disabled={busy === r.press_id}
                      onClick={async () => {
                        if (!await confirmToast(
                          'Сменить ключ? Старый сразу перестанет работать — новый '
                          + 'придётся вписать в программу на моноблоке.',
                          { danger: true, confirmText: 'Сменить' })) return;
                        act(r.press_id, 'rotate');
                      }}>
                      {t('Сменить ключ')}
                    </button>
                  </>
                ) : (
                  <button
                    style={{ ...BTN, background: 'var(--accent)', color: '#fff',
                             borderColor: 'var(--accent)', fontWeight: 600 }}
                    disabled={busy === r.press_id}
                    onClick={() => act(r.press_id, 'create')}>
                    {t('Выдать ключ')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── Разбор непринятых сообщений пресса ──────────────────────────────────────
//
// Листы отпрессованы — работа сделана физически. Но принять сообщение MES не
// смог: технолог снял задание, смену закрыли из браузера, склад успел забрать,
// бирка освобождена. Программа у пресса это исправить не может: у неё нет ни
// прав, ни человека рядом.
//
// Отдать ей отказ и забыть — значит потерять смену, и обнаружится это при
// сверке остатка склада, без единой записи о том, где именно. Поэтому такие
// сообщения ждут здесь.
//
// «Принять» не заставляет данные подойти: она пробует применить их ещё раз.
// Сначала админ устраняет причину — возвращает позицию в работу, открывает
// смену, — потом принимает. Не устранил — вернётся тот же отказ.
function DeviceRejects() {
  const [rows, setRows]   = useState([]);
  const [pending, setPending] = useState(0);
  const [load, setLoad]   = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busy, setBusy]   = useState(null);
  const [open, setOpen]   = useState({});
  const [notes, setNotes] = useState({});

  const reload = useCallback(() => {
    setLoad(true);
    productionAPI.getDeviceRejects(filter)
      .then(r => { setRows(r.data.results || []); setPending(r.data.pending || 0); })
      .catch(() => toast.error(t('Не удалось загрузить список')))
      .finally(() => setLoad(false));
  }, [filter]);
  useEffect(() => { reload(); }, [reload]);

  // Какую смену человек назвал листу, у которого её не нашли ничем.
  const [jobs, setJobs] = useState({});

  const act = async (row, action) => {
    if (action === 'dismiss' && !await confirmToast(
      'Отклонить сообщение? Данные в базу не попадут — так делают, когда листы '
      + 'уже учтены другим путём.', { danger: true, confirmText: 'Отклонить' })) return;
    if (action === 'transfer' && !await confirmToast(
      'Перенести сообщение на пресс «' + (row.transfer_to?.press || '') + '» и '
      + 'применить там? Листы запишутся в смену того пресса.',
      { confirmText: 'Перенести' })) return;
    setBusy(row.id);
    try {
      await productionAPI.resolveDeviceReject(
        row.id, action, notes[row.id] || '',
        action === 'transfer' ? undefined : (jobs[row.id] || undefined));
      toast.success(action === 'apply' ? t('Принято')
        : action === 'transfer' ? t('Перенесено') : t('Отклонено'));
      reload();
    } catch (e) {
      const d = e.response?.data || {};
      // Причина не устранена — так и говорим: приём ничего не подгоняет
      toast.error(d.error || 'Не удалось применить');
    } finally { setBusy(null); }
  };

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit',
        hour: '2-digit', minute: '2-digit' })
    : '—';

  // Что именно человек должен поправить, прежде чем принимать. Без этой
  // подсказки «Принять» превращается в кнопку, которую жмут наугад.
  const WHAT_TO_DO = {
    job_not_active:       'Смена закрыта. Если листы ещё не учтены — поправьте выпуск смены на прессе, а это сообщение отклоните.',
    queue_item_not_found: 'Задания нет на этом прессе. Верните позицию прессу в очереди, потом примите.',
    queue_item_done:      'Позиция закрыта. Верните её в работу (Заявки → вернуть в очередь), потом примите.',
    job_already_active:   'По заданию уже идёт другая смена. Закройте её, потом примите.',
    already_taken:        'Склад уже принял смену — править её нельзя. Расхождение правят на складе.',
    sheet_code_invalid:   'Бирка не выдана этому прессу, уже наклеена или освобождена. Сверьте бирку на листе.',
    pin_not_found:        'Работника с таким ПИНом нет. Заведите ПИН тому, кто работал, потом примите.',
    grade_over_produced:  'Сортов больше, чем выпущено листов. Сперва проверьте выпуск смены.',
    not_enough_free_sheets: 'Столько свободных листов у смены нет — часть уже забрал склад.',
    // Смену моноблока MES не принял — чаще всего потому, что её начали в
    // браузере, и своего номера у моноблока не появилось. Лист при этом
    // отпрессован и лежит на складе с биркой, так что назвать смену должен
    // человек: по времени и бирке видно, кто работал.
    job_ref_unresolved:   'Смена этого листа в MES не открыта — её начали из браузера. Выберите смену ниже: лист запишется в неё.',
    job_unresolved:       'Смену листа найти нечем. Выберите её ниже — по времени и бирке видно, кто работал.',
    job_not_found:        'Смены с таким номером нет. Выберите смену ниже.',
    job_not_started:      'Задание не в работе. Верните позицию в работу, потом примите.',
    queue_item_missing:   'У смены нет позиции заказа — принимать некуда. Отклоните: лист учитывают вручную.',
    no_active_job:        'Открытой смены у задания нет — значит её уже закрыли. Отклоните: закрывать второй раз нечего.',
    nothing_to_unmark:    'Снимать нечего: такой отметки сорта у смены нет. Отклоните.',
    press_already_stopped:'Остановка уже отмечена — время учтено. Отклоните.',
    job_not_on_press:     'Эта смена идёт на другом прессе. Выберите смену того же пресса.',
    sheet_codes_mismatch: 'Бирок и листов разное количество — данные прислали неполными. Отклоните: лист учитывают вручную.',
    bad_event:            'Сообщение пришло в неизвестном виде — программа у пресса прислала его неправильно. Отклоните и сообщите разработчику.',
    unknown_op:           'Такой операции MES не знает — скорее всего, у пресса старая версия программы. Отклоните и обновите её.',
  };

  const BTN = {
    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--muted)', padding: '8px 14px', fontSize: 13, cursor: 'pointer',
    fontFamily: 'inherit', whiteSpace: 'nowrap',
  };
  // Сводка: сколько листов из ждущих уйдёт в закрытые смены и за какие даты
  const lateSummary = (() => {
    const cl = rows.filter(r => r.status === 'pending' && r.late_info
      && r.late_info.closed && r.late_info.date);
    if (!cl.length) return '';
    const n = cl.reduce((a, r) => a + (parseInt(r.payload?.count, 10)
      || (r.payload?.sheet_codes || []).length || 1), 0);
    const ds = cl.map(r => r.late_info.date)
      .sort((a, b) => a.split('.').reverse().join('').localeCompare(b.split('.').reverse().join('')));
    const range = ds[0] === ds[ds.length - 1] ? ds[0] : ds[0] + '–' + ds[ds.length - 1];
    return `${n} листов уйдут в закрытые смены за ${range}`;
  })();
  const TABS = [
    ['pending',   'Ждут разбора'],
    ['applied',   'Принятые'],
    ['dismissed', 'Отклонённые'],
    ['all',       'Все'],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.55, maxWidth: 820 }}>
        Программа у пресса присылает готовые листы, брак и закрытие смены. Если
        MES принять сообщение не смог — задание сняли, смену закрыли из браузера,
        склад успел забрать, — оно не пропадает, а ждёт здесь.
        <br />
        Листы при этом уже отпрессованы. Сначала устраните причину, потом
        нажмите «Принять»: она не подгоняет данные, а пробует применить их заново.
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} style={{
            ...BTN,
            background: filter === k ? 'var(--accent)' : 'var(--bg)',
            color: filter === k ? '#fff' : 'var(--muted)',
            borderColor: filter === k ? 'var(--accent)' : 'var(--border)',
            fontWeight: filter === k ? 600 : 400,
          }}>
            {label}
            {k === 'pending' && pending > 0 ? ` · ${pending}` : ''}
          </button>
        ))}
        <button style={{ ...BTN, marginLeft: 'auto' }} onClick={reload}>🔄</button>
      </div>

      {filter === 'pending' && lateSummary && (
        <div style={{ color: '#f59e0b', fontSize: 13 }}>{lateSummary}</div>
      )}

      {load ? (
        <p style={{ color: 'var(--muted)', fontSize: 13 }}>{t('Загрузка...')}</p>
      ) : rows.length === 0 ? (
        <div style={{
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '22px 18px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
          <div style={{ color: 'var(--muted)', fontSize: 14 }}>
            {filter === 'pending'
              ? 'Непринятых сообщений нет — прессы пишут в базу без отказов'
              : 'Здесь пусто'}
          </div>
        </div>
      ) : rows.map(r => {
        const isOpen = !!open[r.id];
        const done = r.status !== 'pending';
        return (
          <div key={r.id} style={{
            background: 'var(--bg)', borderRadius: 10, padding: '13px 15px',
            border: '1px solid ' + (done ? 'var(--border)' : 'var(--danger)'),
            display: 'flex', flexDirection: 'column', gap: 9,
            opacity: done ? 0.7 : 1,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <b style={{ color: 'var(--text)', fontSize: 15 }}>{r.press}</b>
              <span style={{
                fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '2px 8px',
                background: 'var(--card)', color: 'var(--muted)',
                fontFamily: 'monospace',
              }}>{r.op}</span>
              <span style={{
                fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '2px 8px',
                background: done ? '#22c55e20' : '#ef444420',
                color: done ? '#22c55e' : '#ef4444',
              }}>
                {r.status === 'pending' ? 'ЖДЁТ РАЗБОРА'
                  : r.status === 'applied' ? 'ПРИНЯТО' : 'ОТКЛОНЕНО'}
              </span>
              <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 'auto' }}>
                {/* Время по часам моноблока: смену могли отработать вчера, а
                    досылали сегодня — разбирать надо по первому */}
                со станка: {fmt(r.occurred_at)} · отложено: {fmt(r.created_at)}
              </span>
            </div>

            <div style={{ color: 'var(--text)', fontSize: 14 }}>
              {r.error_message}
              <span style={{ color: 'var(--dim)', fontFamily: 'monospace',
                             fontSize: 12, marginLeft: 8 }}>{r.error_code}</span>
            </div>

            {!done && WHAT_TO_DO[r.error_code] && (
              <div style={{
                background: '#f59e0b14', border: '1px solid #f59e0b40',
                borderRadius: 8, padding: '9px 12px',
                color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.5,
              }}>
                <b style={{ color: '#f59e0b' }}>Что сделать: </b>
                {WHAT_TO_DO[r.error_code]}
              </div>
            )}

            {/* Смену листа не нашли ничем — но она известна по времени:
                та, что шла в ту минуту. Показываем соседние смены пресса и
                даём выбрать. Без этого «укажите номер смены» — просьба,
                которую невозможно выполнить: номера негде взять. */}
            {!done && (r.job_options || []).length > 0 && (
              <div style={{
                background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '9px 12px',
              }}>
                <div style={{ color: 'var(--muted)', fontSize: 12.5, marginBottom: 7 }}>
                  В какую смену записать лист:
                </div>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                  {r.job_options.map(j => {
                    const on = String(jobs[r.id] || '') === String(j.id);
                    return (
                      <button key={j.id}
                        onClick={() => setJobs(s2 => ({
                          ...s2, [r.id]: on ? '' : j.id }))}
                        style={{
                          ...BTN,
                          textAlign: 'left', whiteSpace: 'normal',
                          background: on ? 'var(--accent)' : 'var(--bg)',
                          color: on ? '#fff' : 'var(--muted)',
                          borderColor: on ? 'var(--accent)'
                            : j.covers ? '#22c55e60' : 'var(--border)',
                        }}>
                        <b>{j.started_at}</b>
                        {j.completed_at ? ' – ' + j.completed_at : ' – идёт'}
                        {j.order ? ' · ' + j.order : ''}
                        {j.operator ? ' · ' + j.operator : ''}
                        {' · '}{j.done}/{j.planned}
                        {j.covers && !on
                          ? <span style={{ color: '#22c55e' }}> · шла в это время</span>
                          : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {done && r.note && (
              <div style={{ color: 'var(--dim)', fontSize: 12.5 }}>
                {r.resolved_by ? r.resolved_by + ': ' : ''}{r.note}
              </div>
            )}

            {!done && r.late_info && r.late_info.date && (
              <div style={{ color: '#f59e0b', fontSize: 12.5, margin: '0 0 8px' }}>
                {r.late_info.closed
                  ? `Уйдёт в закрытую смену от ${r.late_info.date} (№${r.late_info.number}) — на складе появится смена с этой датой`
                  : r.late_info.closed === false
                    ? `Уйдёт в открытую смену №${r.late_info.number}`
                    : `Смена не определена; работа сделана ${r.late_info.date}`}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button style={BTN} onClick={() => setOpen(s => ({ ...s, [r.id]: !s[r.id] }))}>
                {isOpen ? 'Скрыть, что прислали' : 'Что прислали'}
              </button>
              {!done && (
                <>
                  <input
                    value={notes[r.id] || ''}
                    onChange={e => setNotes(s => ({ ...s, [r.id]: e.target.value }))}
                    placeholder={t('примечание — что именно поправили')}
                    style={{
                      flex: '1 1 220px', minWidth: 0, background: 'var(--card)',
                      border: '1px solid var(--border)', borderRadius: 8,
                      color: 'var(--text)', padding: '8px 11px', fontSize: 13,
                      fontFamily: 'inherit',
                    }} />
                  <button
                    style={{ ...BTN, background: 'var(--accent)', color: '#fff',
                             borderColor: 'var(--accent)', fontWeight: 600 }}
                    disabled={busy === r.id}
                    onClick={() => act(r, 'apply')}>
                    {busy === r.id ? '…' : t('Принять')}
                  </button>
                  {r.transfer_to && (
                    <button
                      style={{ ...BTN, borderColor: 'var(--accent)', color: 'var(--accent)' }}
                      disabled={busy === r.id}
                      title={t('Сообщение пришло с ключа другого пресса — '
                               + 'перенести на пресс его смены')}
                      onClick={() => act(r, 'transfer')}>
                      {t('Перенести на пресс')} {r.transfer_to.press}
                    </button>
                  )}
                  <button
                    style={{ ...BTN, borderColor: 'var(--danger)', color: 'var(--danger)' }}
                    disabled={busy === r.id}
                    onClick={() => act(r, 'dismiss')}>
                    {t('Отклонить')}
                  </button>
                </>
              )}
            </div>

            {isOpen && (
              <pre style={{
                background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '10px 12px', margin: 0,
                fontSize: 12, color: 'var(--muted)', overflowX: 'auto',
                lineHeight: 1.5,
              }}>{JSON.stringify(r.payload, null, 2)}</pre>
            )}
          </div>
        );
      })}
    </div>
  );
}


export default function AdminPage() {
  const [section, setSection] = useState('home');
  const navigate = useNavigate();
  // Счётчик в меню: без него очередь разбора копится незамеченной — админ
  // просто не знает, что смена с пресса чего-то ждёт
  const [pending, setPending] = useState(0);

  useEffect(() => {
    const load = () => productionAPI.getDeviceRejects('pending')
      .then(r => setPending(r.data.pending || 0))
      .catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [section]);

  const sections = SECTIONS.map(s => ({
    ...s,
    items: s.items.map(it => it.key === 'rejects' && pending > 0
      ? { ...it, label: `${it.label} · ${pending}` }
      : it),
  }));

  // Рабочие места ролей. Раньше все восемь висели в общем меню у одного
  // администратора — чужая работа рядом со своей, пятнадцать пунктов подряд.
  // Место им здесь: это его главный экран, а не постоянная колонка.
  const ROLE_TILES = ROLES.map(r => ({
    key: r.route, icon: r.icon, title: r.label, small: true, color: 'var(--accent)',
  }));

  const HOME_TILES = [
    { key: 'rejects', icon: '📥', title: 'Разбор с прессов',
      hint: 'Смены, которые программа принять не смогла — их разбирает администратор',
      count: pending, color: '#ef4444' },
    { key: 'stats', icon: '📊', title: 'Статистика',
      hint: 'Состояние завода в цифрах', color: 'var(--accent)' },
  ];

  return (
    <Layout title={t("Администратор")} sections={sections} section={section} onSection={setSection}>
      {section !== 'home' && (
        <BackHome onBack={() => setSection('home')} />
      )}

      {/* Главное — это карта, а не этаж: всё, что есть у администратора, видно
          на одном экране и подписано, зачем оно. Ровно те же пункты стоят в
          боковом меню, так что второй раз сюда заходить не обязательно. */}
      {section === 'home' && (<>
        <TilesHead title="Что делаем сегодня"
          hint="Числа рядом — сколько ждёт разбора" />
        <TileGrid tiles={HOME_TILES} onPick={setSection} />
        <div style={{ marginTop: 22 }}>
          <TilesHead title="Рабочие места"
            hint="Открыть экран любой роли — посмотреть или помочь" />
          <TileGrid tiles={ROLE_TILES} onPick={(to) => navigate(to)} min={200} />
        </div>
        <div style={{ marginTop: 22 }}>
          <TilesHead title="Настройка"
            hint="Трогают редко. Всё это же есть в меню слева — здесь с подсказками." />
          {/* Обмен с 1С — не раздел этой страницы, а отдельный экран: очередь
              смотрят подолгу, и место ей рядом с журналом. Плитка здесь
              только чтобы её было где найти. */}
          <TileGrid tiles={SETUP_TILES} min={230}
            onPick={k => (k === 'integration' ? navigate('/integration') : setSection(k))} />
        </div>
      </>)}

      {section === 'testdata' && <TestDataPanel />}
      {section === 'general' && <GeneralSettingsPanel />}
      {section === 'users'   && <UsersPanel />}
      {section === 'recipes' && <RecipesPanel />}
      {section === 'norms'   && <PressNormPanel />}
      {section === 'stops'   && <StopReasonsPanel />}
      {section === 'quality' && <QualityRefsPanel />}
      {section === 'wh'      && <WarehouseSettings />}
      {section === 'devices' && <PressDevices />}
      {section === 'ai'      && <AiThresholdsPanel />}
      {section === 'rejects' && <DeviceRejects />}
      {section === 'stats'   && <QuickStats />}
      {section === 'trash'   && <DeletionMarkPanel />}
    </Layout>
  );
}
